// src/Tutorial.js
/**
 * Interactive Tutorial for Phutball
 * Structure is a carbon copy of App.js (local multiplayer) with tutorial instructions in LogPanel
 */
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import GameBoard from './GameBoard';
import Header from './Header';
import LogPanel from './LogPanel';
import HistoryPanel from './HistoryPanel';
import CoordinateSystemIcon from './CoordinateSystemIcon';
import { formatBoardCoordinate } from './coordinateFormatting';
import { trackEvent } from './telemetry';
import './App.css';
import './Tutorial.css';

const TILE_TYPE = { EMPTY: 0, PHUTBALL: -1, PHUTBALL_MAN: 1 };

// --- Stacked/mobile layout utilities (mirrored from App.js) ---
const TUTORIAL_PANEL_CONFIG = [
  { id: 'log', label: 'Tutorial', shortLabel: 'Tutorial' },
  { id: 'board', label: 'Game Board', shortLabel: 'Board' },
  { id: 'history', label: 'History', shortLabel: 'History' },
];

const clampTutorialPanelIndex = (value) => Math.max(0, Math.min(TUTORIAL_PANEL_CONFIG.length - 1, value));

const shouldUseStackedLayout = () => {
  if (typeof window === 'undefined') return false;
  const { innerWidth, innerHeight } = window;
  const width = typeof innerWidth === 'number' ? innerWidth : window.screen?.width ?? 0;
  const height = typeof innerHeight === 'number' ? innerHeight : window.screen?.height ?? 0;
  if (!width || !height) return width <= 1200;
  const portrait = height > width;
  return width <= 1200 || portrait;
};

const POINTER_SWIPE_FRACTION = 0.3;
const WHEEL_SWIPE_FRACTION = 0.5;
const MIN_SWIPE_DISTANCE = 120;
const WHEEL_COOLDOWN_MS = 420;

const computeSwipeThreshold = (element, fraction = POINTER_SWIPE_FRACTION) => {
  const width = element?.clientWidth ?? window.innerWidth ?? document.documentElement?.clientWidth ?? 0;
  const fractionTarget = width > 0 ? width * fraction : 0;
  if (fractionTarget > 0) return Math.max(MIN_SWIPE_DISTANCE, fractionTarget);
  return MIN_SWIPE_DISTANCE;
};

// Board dimensions
const ROWS = 21;
const COLS = 15;
const CENTER_ROW = 10;
const CENTER_COL = 7;

// Helper to create empty board
const createEmptyBoard = () => {
  const board = [];
  for (let r = 0; r < ROWS; r++) {
    const row = [];
    for (let c = 0; c < COLS; c++) {
      row.push(TILE_TYPE.EMPTY);
    }
    board.push(row);
  }
  return board;
};

// Helper to set pieces on board
const setBoardPieces = (board, pieces) => {
  const newBoard = board.map(row => [...row]);
  pieces.forEach(({ pos, type }) => {
    const [r, c] = pos;
    if (r >= 0 && r < ROWS && c >= 0 && c < COLS) {
      newBoard[r][c] = type;
    }
  });
  return newBoard;
};

// ASCII board text for log breadcrumbs (matches PhutballEngine.textRender format)
const TILE_CHARS = { [TILE_TYPE.EMPTY]: '┼', [TILE_TYPE.PHUTBALL]: 'O', [TILE_TYPE.PHUTBALL_MAN]: 'x' };
const tutorialBoardText = (board) => {
  const grid = board.map((row, r) => {
    if (r === 0) return '+'.repeat(COLS);
    if (r === ROWS - 1) return '-'.repeat(COLS);
    return row.map(tile => TILE_CHARS[tile] ?? '?').join('');
  });
  return grid.join('\n');
};

// Genesis tutorial - Quick Start (7 steps)
// The teaching order is the shibboleth:
// covenant, consent, discernment, power, discretion, fidelity, delight
const GENESIS_STEPS = [
  {
    // covenant: the shared field
    id: 'genesis-field',
    board: [],
    phutballPos: [CENTER_ROW, CENTER_COL],
    instruction: "The Field",
    detail: (mobile) => mobile
      ? "Score by driving the ball across your opponent's endzone. You share the same board, the same stones, the same rules. The only difference is direction. Your winning gate is always up.\n\nSwipe or use the dots above to switch between panels. The controls at the bottom of this panel light up orange to show what you need to do next."
      : "Score by driving the ball across your opponent's endzone. You share the same board, the same stones, the same rules. The only difference is direction. Your winning gate is always up.\n\nThe controls at the bottom of this panel light up orange to show what you need to do next.",
    expectedAction: { type: 'continue' },
  },
  {
    // consent: deliberate placement
    id: 'genesis-place',
    board: [],
    phutballPos: [CENTER_ROW, CENTER_COL],
    instruction: "Place a Stone",
    detail: (mobile) => mobile
      ? "See the pulsing ring on the board? That's your guiding star. Tap Place to place a stone there. Every stone is shared. Your opponent can use it too."
      : "See the pulsing ring on the board? That's your guiding star. Press P to place a stone there. Every stone is shared. Your opponent can use it too.",
    cursorStart: [CENTER_ROW - 1, CENTER_COL],
    expectedAction: { type: 'place', pos: [CENTER_ROW - 1, CENTER_COL] },
    highlightPos: [CENTER_ROW - 1, CENTER_COL],
  },
  {
    // discernment: look before you act
    id: 'genesis-look',
    board: [],
    phutballPos: [CENTER_ROW, CENTER_COL],
    instruction: "Look First",
    detail: (mobile) => mobile
      ? "Follow the pulse above the ball. Tap it to move the cursor there. The board rewards patience more than speed."
      : "Follow the pulse above the ball. Use W/A/S/D or arrow keys to move the cursor there. You can also click. The board rewards patience more than speed.",
    cursorStart: [CENTER_ROW, CENTER_COL],
    expectedAction: { type: 'move-cursor', target: [CENTER_ROW - 1, CENTER_COL] },
    highlightPos: [CENTER_ROW - 1, CENTER_COL],
  },
  {
    // power: the jump
    id: 'genesis-jump',
    board: [
      { pos: [CENTER_ROW - 1, CENTER_COL], type: TILE_TYPE.PHUTBALL_MAN },
      { pos: [CENTER_ROW - 2, CENTER_COL], type: TILE_TYPE.PHUTBALL_MAN },
      { pos: [CENTER_ROW - 3, CENTER_COL], type: TILE_TYPE.PHUTBALL_MAN },
      { pos: [CENTER_ROW - 4, CENTER_COL], type: TILE_TYPE.PHUTBALL_MAN },
      { pos: [CENTER_ROW - 5, CENTER_COL], type: TILE_TYPE.PHUTBALL_MAN },
      { pos: [CENTER_ROW - 6, CENTER_COL], type: TILE_TYPE.PHUTBALL_MAN },
    ],
    phutballPos: [CENTER_ROW, CENTER_COL],
    instruction: "Jump",
    detail: (mobile) => mobile
      ? "The ball moves by jumping over stones. The flickering ring marks the landing. Tap it, then tap Jump. A single jump clears an entire contiguous line of stones."
      : "The ball moves by jumping over stones. The flickering ring marks the landing. Move the cursor there and press J. A single jump clears an entire contiguous line of stones.",
    cursorStart: [CENTER_ROW, CENTER_COL],
    expectedAction: { type: 'jump', pos: [CENTER_ROW - 7, CENTER_COL] },
    highlightPos: [CENTER_ROW - 7, CENTER_COL],
  },
  {
    // discretion: power costs something
    id: 'genesis-cost',
    board: [
      { pos: [CENTER_ROW - 1, CENTER_COL], type: TILE_TYPE.PHUTBALL_MAN },
      { pos: [CENTER_ROW - 2, CENTER_COL], type: TILE_TYPE.PHUTBALL_MAN },
      { pos: [CENTER_ROW - 3, CENTER_COL], type: TILE_TYPE.PHUTBALL_MAN },
      { pos: [CENTER_ROW - 4, CENTER_COL], type: TILE_TYPE.PHUTBALL_MAN },
      { pos: [CENTER_ROW - 5, CENTER_COL], type: TILE_TYPE.PHUTBALL_MAN },
      { pos: [CENTER_ROW - 6, CENTER_COL], type: TILE_TYPE.PHUTBALL_MAN },
    ],
    phutballPos: [CENTER_ROW, CENTER_COL],
    instruction: "The Cost",
    detail: "Every stone you jump is consumed and removed from the board. Power costs something. Choose your path carefully.",
    cursorStart: [CENTER_ROW - 7, CENTER_COL],
    stagedJump: { path: [[CENTER_ROW - 7, CENTER_COL]], startPos: [CENTER_ROW, CENTER_COL] },
    expectedAction: { type: 'confirm' },
  },
  {
    // fidelity: mean what you move
    // Three stones toward endzone. Pre-staged: two valid north jumps, then an illegal third jump.
    // Undo the illegal jump, then jump north to score.
    id: 'genesis-commit',
    board: [
      { pos: [5, CENTER_COL], type: TILE_TYPE.PHUTBALL_MAN },
      { pos: [3, CENTER_COL], type: TILE_TYPE.PHUTBALL_MAN },
      { pos: [1, CENTER_COL], type: TILE_TYPE.PHUTBALL_MAN },
    ],
    phutballPos: [6, CENTER_COL],
    instruction: "Commit",
    detail: (mobile) => mobile
      ? "A wrong jump is staged. Tap Undo to remove it. Find the right path to score. Mean what you move."
      : "A wrong jump is staged. Press Backspace to undo it. Find the right path to score. Mean what you move.",
    cursorStart: [0, CENTER_COL],
    stagedJump: { path: [[4, CENTER_COL], [2, CENTER_COL], [2, CENTER_COL - 1]], startPos: [6, CENTER_COL] },
    expectedAction: { type: 'undo' }, // overridden by actionSequence[0]
    actionSequence: [
      { expectedAction: { type: 'undo' } },
      { expectedAction: { type: 'jump', pos: [0, CENTER_COL] }, highlightSequence: [[4, CENTER_COL], [2, CENTER_COL], [0, CENTER_COL]] },
      { expectedAction: { type: 'confirm' }, stagedJump: { path: [[4, CENTER_COL], [2, CENTER_COL], [0, CENTER_COL]], startPos: [6, CENTER_COL] } },
    ],
  },
  {
    // delight: go play
    id: 'genesis-go',
    board: [],
    phutballPos: [CENTER_ROW - 5, CENTER_COL],
    instruction: "Go Play",
    detail: "There is no passing. You must act each turn. Go find out what Phutball has been hiding from you.",
    expectedAction: { type: 'finish' },
  },
];

// Tutorial steps - A narrative journey through Phutball
const TUTORIAL_STEPS = [
  // ===== INTRO =====
  {
    id: 'intro',
    board: [],
    phutballPos: [CENTER_ROW, CENTER_COL],
    instruction: "Philosopher's Football",
    detail: (mobile) => mobile
      ? "is a two-player zero-sum game with simple rules and complex tactics that emerge from compositional play.\n\nSwipe or use the dots above to switch between panels. Complete the task on each page to advance. The controls at the bottom of this panel light up orange to show what you need to do next."
      : "is a two-player zero-sum game with simple rules and complex tactics that emerge from compositional play.\n\nComplete the task on each page to advance. The controls at the bottom of this panel light up orange to show what you need to do next.",
    expectedAction: { type: 'continue' },
  },
  {
    id: 'intro-goal',
    board: [],
    phutballPos: [CENTER_ROW, CENTER_COL],
    instruction: "The Goal",
    detail: "Players take turns placing stones on the board and consuming them in explosive jump sequences to drive the ball across their opponent's endzone boundary. Player 1 scores on blue; Player 2 scores on red. Your winning gate is always up. The board rotates after each turn.",
    expectedAction: { type: 'continue' },
  },

  // ===== PLACEMENT: P1 =====
  {
    id: 'p1-cursor-keyboard',
    board: [],
    phutballPos: [CENTER_ROW, CENTER_COL],
    instruction: "Moving the Cursor",
    detail: (mobile) => mobile
      ? "See the pulsing ring? That's your guiding star. Tap it to move the cursor there."
      : "See the pulsing ring? That's your guiding star. Use W/A/S/D (or arrow keys) to move the cursor there, or click it directly.",
    cursorStart: [CENTER_ROW, CENTER_COL],
    expectedAction: { type: 'move-cursor', target: [CENTER_ROW - 1, CENTER_COL] },
    highlightPos: [CENTER_ROW - 1, CENTER_COL],
  },
  {
    id: 'p1-cursor-mouse',
    board: [],
    phutballPos: [CENTER_ROW, CENTER_COL],
    instruction: "Mouse Controls",
    detail: (mobile) => mobile
      ? "Tap any cell on the board to move the cursor there. Follow the flickering ring to the position below the ball."
      : "Hover over the board to preview positions. Click to move the cursor. Follow the flickering ring to the position below the ball.",
    cursorStart: [CENTER_ROW - 1, CENTER_COL],
    expectedAction: { type: 'click', target: [CENTER_ROW + 1, CENTER_COL] },
    highlightPos: [CENTER_ROW + 1, CENTER_COL],
  },
  {
    id: 'board-nav-fit',
    board: [],
    phutballPos: [CENTER_ROW, CENTER_COL],
    instruction: "Board Navigation",
    detail: (mobile) => mobile
      ? "Pinch to zoom and drag to pan the board. Try it now, then tap the ⌖ button below the board to fit it back into view."
      : "Scroll to zoom and drag to pan the board. Try it now, then press F to fit the board back into view.",
    cursorStart: [CENTER_ROW + 1, CENTER_COL],
    expectedAction: { type: 'key', key: 'f' },
    requiresTransform: true,
  },
  {
    id: 'board-nav-center',
    board: [],
    phutballPos: [CENTER_ROW, CENTER_COL],
    instruction: "Centering",
    detail: (mobile) => mobile
      ? null
      : "Drag the board off-center, then press C to snap it back. This centers at your current zoom level.",
    cursorStart: [CENTER_ROW + 1, CENTER_COL],
    expectedAction: { type: 'key', key: 'c' },
    requiresTransform: true,
    desktopOnly: true,
  },
  {
    id: 'p1-place-action',
    board: [],
    phutballPos: [CENTER_ROW, CENTER_COL],
    instruction: "Placing a Stone",
    detail: (mobile) => mobile
      ? "Place your first stone above the ball. The flashing beacon marks the spot. Tap it, then tap Place to stage your placement. (Tap Undo to destage.)"
      : "Place your first stone above the ball. The flashing beacon marks the spot. Move the cursor there and press P to stage your placement. (Backspace to destage.)",
    cursorStart: [CENTER_ROW, CENTER_COL],
    expectedAction: { type: 'place', pos: [CENTER_ROW - 1, CENTER_COL] },
    highlightPos: [CENTER_ROW - 1, CENTER_COL],
  },
  {
    id: 'p1-place-confirm',
    board: [],
    phutballPos: [CENTER_ROW, CENTER_COL],
    instruction: "Confirm",
    detail: (mobile) => mobile
      ? "Tap Confirm to commit the move."
      : "Press ENTER to commit the move.",
    cursorStart: [CENTER_ROW, CENTER_COL],
    stagedMove: { type: 'placement', path: [[CENTER_ROW - 1, CENTER_COL]] },
    expectedAction: { type: 'confirm' },
  },

  // ===== NEUTRAL PIECES =====
  {
    id: 'neutral-pieces',
    board: [
      { pos: [CENTER_ROW - 1, CENTER_COL], type: TILE_TYPE.PHUTBALL_MAN },
    ],
    phutballPos: [CENTER_ROW, CENTER_COL],
    instruction: "Neutral Pieces",
    detail: "Important: the stones are neutral. Both players place and jump the same pieces. A stone you place might help your opponent, or be consumed by their jump. This shared resource creates Phutball's tactical depth.",
    expectedAction: { type: 'continue' },
  },

  // ===== CAN'T PLACE ON BALL OR ENDZONES =====
  {
    id: 'invalid-intro',
    board: [],
    phutballPos: [CENTER_ROW, CENTER_COL],
    instruction: "Forbidden Placements",
    detail: "You cannot place stones on the ball or in the deep endzone rows (+∞/-∞ in endzone relative mode). Try placing at the flashing beacon in the deep row to see it rejected.",
    cursorStart: [CENTER_ROW, CENTER_COL],
    expectedAction: { type: 'invalid-place', targetRow: 0 },
    highlightPos: [0, CENTER_COL],
  },

  // ===== EIGHT DOORS =====
  // Animated demo showing 8 directions from center
  {
    id: 'eight-doors',
    board: [
      // 8 stones surrounding the ball, one in each direction
      { pos: [CENTER_ROW - 1, CENTER_COL], type: TILE_TYPE.PHUTBALL_MAN },     // N
      { pos: [CENTER_ROW - 1, CENTER_COL + 1], type: TILE_TYPE.PHUTBALL_MAN }, // NE
      { pos: [CENTER_ROW, CENTER_COL + 1], type: TILE_TYPE.PHUTBALL_MAN },     // E
      { pos: [CENTER_ROW + 1, CENTER_COL + 1], type: TILE_TYPE.PHUTBALL_MAN }, // SE
      { pos: [CENTER_ROW + 1, CENTER_COL], type: TILE_TYPE.PHUTBALL_MAN },     // S
      { pos: [CENTER_ROW + 1, CENTER_COL - 1], type: TILE_TYPE.PHUTBALL_MAN }, // SW
      { pos: [CENTER_ROW, CENTER_COL - 1], type: TILE_TYPE.PHUTBALL_MAN },     // W
      { pos: [CENTER_ROW - 1, CENTER_COL - 1], type: TILE_TYPE.PHUTBALL_MAN }, // NW
    ],
    phutballPos: [CENTER_ROW, CENTER_COL],
    instruction: "Eight Doors",
    detail: "Every position has eight doors: the cardinal and diagonal directions. As stones that are jumped over are consumed, each door can only be used once per jump sequence. Watch as the ball demonstrates all eight.",
    expectedAction: { type: 'continue' },
    animatedDemo: 'eight-doors',
  },

  // ===== JUMPING RULES =====
  {
    id: 'jump-rules-intro',
    board: [
      { pos: [CENTER_ROW - 1, CENTER_COL], type: TILE_TYPE.PHUTBALL_MAN },
    ],
    phutballPos: [CENTER_ROW, CENTER_COL],
    instruction: "Jumping Rules",
    detail: "The ball only moves by jumping, and jumping requires consuming at least one stone. You can't move the ball to an empty adjacent position: it must leap over stones to travel.",
    expectedAction: { type: 'continue' },
  },

  // ===== CLEARING CONTIGUOUS LINES =====
  {
    id: 'line-clear-intro',
    board: [
      { pos: [CENTER_ROW - 1, CENTER_COL], type: TILE_TYPE.PHUTBALL_MAN },
      { pos: [CENTER_ROW - 2, CENTER_COL], type: TILE_TYPE.PHUTBALL_MAN },
      { pos: [CENTER_ROW - 3, CENTER_COL], type: TILE_TYPE.PHUTBALL_MAN },
      { pos: [CENTER_ROW - 4, CENTER_COL], type: TILE_TYPE.PHUTBALL_MAN },
    ],
    phutballPos: [CENTER_ROW, CENTER_COL],
    instruction: "Clearing Lines",
    detail: "A single jump clears every stone along a contiguous line. The ball leaps over the entire line and lands on the first empty position beyond.",
    expectedAction: { type: 'continue' },
  },
  {
    id: 'line-clear-jump',
    board: [
      { pos: [CENTER_ROW - 1, CENTER_COL], type: TILE_TYPE.PHUTBALL_MAN },
      { pos: [CENTER_ROW - 2, CENTER_COL], type: TILE_TYPE.PHUTBALL_MAN },
      { pos: [CENTER_ROW - 3, CENTER_COL], type: TILE_TYPE.PHUTBALL_MAN },
      { pos: [CENTER_ROW - 4, CENTER_COL], type: TILE_TYPE.PHUTBALL_MAN },
    ],
    phutballPos: [CENTER_ROW, CENTER_COL],
    instruction: "Clear All Four",
    detail: "Jump to the first empty position beyond the line of stones.",
    cursorStart: [CENTER_ROW, CENTER_COL],
    expectedAction: { type: 'jump', pos: [CENTER_ROW - 5, CENTER_COL] },
    highlightPos: [CENTER_ROW - 5, CENTER_COL],
  },
  {
    id: 'line-clear-confirm',
    board: [
      { pos: [CENTER_ROW - 1, CENTER_COL], type: TILE_TYPE.PHUTBALL_MAN },
      { pos: [CENTER_ROW - 2, CENTER_COL], type: TILE_TYPE.PHUTBALL_MAN },
      { pos: [CENTER_ROW - 3, CENTER_COL], type: TILE_TYPE.PHUTBALL_MAN },
      { pos: [CENTER_ROW - 4, CENTER_COL], type: TILE_TYPE.PHUTBALL_MAN },
    ],
    phutballPos: [CENTER_ROW, CENTER_COL],
    instruction: "Execute",
    detail: (mobile) => mobile
      ? "Tap Confirm. All four stones vanish in a single jump."
      : "Press ENTER. All four stones vanish in a single jump.",
    cursorStart: [CENTER_ROW - 5, CENTER_COL],
    stagedJump: { path: [[CENTER_ROW - 5, CENTER_COL]], startPos: [CENTER_ROW, CENTER_COL] },
    expectedAction: { type: 'confirm' },
  },

  // ===== DRAMATIC SCORING SEQUENCE =====
  // Zigzag path: (10,7) → (7,4) [NW] → (3,4) [N] → (0,7) [NE]
  // Stones needed for valid jumps:
  // - NW jump: (9,6), (8,5)
  // - N jump: (6,4), (5,4), (4,4)
  // - NE jump: (2,5), (1,6)
  {
    id: 'dramatic-intro',
    board: [
      { pos: [9, 6], type: TILE_TYPE.PHUTBALL_MAN },
      { pos: [8, 5], type: TILE_TYPE.PHUTBALL_MAN },
      { pos: [6, 4], type: TILE_TYPE.PHUTBALL_MAN },
      { pos: [5, 4], type: TILE_TYPE.PHUTBALL_MAN },
      { pos: [4, 4], type: TILE_TYPE.PHUTBALL_MAN },
      { pos: [2, 5], type: TILE_TYPE.PHUTBALL_MAN },
      { pos: [1, 6], type: TILE_TYPE.PHUTBALL_MAN },
    ],
    phutballPos: [10, 7],
    instruction: "The Long Kick",
    detail: "The ball moves by jumping OVER stones, landing on the empty position beyond. Each jump consumes the stones it crosses. Here's a path that zigzags up the field to score.",
    expectedAction: { type: 'continue' },
  },
  {
    id: 'dramatic-jump-sequence',
    board: [
      { pos: [9, 6], type: TILE_TYPE.PHUTBALL_MAN },
      { pos: [8, 5], type: TILE_TYPE.PHUTBALL_MAN },
      { pos: [6, 4], type: TILE_TYPE.PHUTBALL_MAN },
      { pos: [5, 4], type: TILE_TYPE.PHUTBALL_MAN },
      { pos: [4, 4], type: TILE_TYPE.PHUTBALL_MAN },
      { pos: [2, 5], type: TILE_TYPE.PHUTBALL_MAN },
      { pos: [1, 6], type: TILE_TYPE.PHUTBALL_MAN },
    ],
    phutballPos: [10, 7],
    instruction: "Chain the Jumps",
    detail: (mobile) => mobile
      ? "Stage three jumps: northwest, then north over three stones, then northeast into the goal. Follow the beacon and tap Jump."
      : "Stage three jumps: northwest, then north over three stones, then northeast into the goal. Follow the beacon and press J.",
    cursorStart: [10, 7],
    expectedAction: { type: 'jump', pos: [0, 7] },
    highlightSequence: [[7, 4], [3, 4], [0, 7]],  // NW, N, NE into goal
  },
  {
    id: 'dramatic-confirm',
    board: [
      { pos: [9, 6], type: TILE_TYPE.PHUTBALL_MAN },
      { pos: [8, 5], type: TILE_TYPE.PHUTBALL_MAN },
      { pos: [6, 4], type: TILE_TYPE.PHUTBALL_MAN },
      { pos: [5, 4], type: TILE_TYPE.PHUTBALL_MAN },
      { pos: [4, 4], type: TILE_TYPE.PHUTBALL_MAN },
      { pos: [2, 5], type: TILE_TYPE.PHUTBALL_MAN },
      { pos: [1, 6], type: TILE_TYPE.PHUTBALL_MAN },
    ],
    phutballPos: [10, 7],
    instruction: "Score!",
    detail: (mobile) => mobile
      ? "Tap Confirm to execute the winning sequence. Three direction changes, seven stones consumed."
      : "Press ENTER to execute the winning sequence. Three direction changes, seven stones consumed.",
    cursorStart: [0, 7],
    stagedJump: { path: [[7, 4], [3, 4], [0, 7]], startPos: [10, 7] },
    expectedAction: { type: 'confirm' },
  },

  // ===== INVALID JUMP FEEDBACK =====
  {
    id: 'invalid-jump-try',
    board: [
      { pos: [CENTER_ROW - 1, CENTER_COL], type: TILE_TYPE.PHUTBALL_MAN },
    ],
    phutballPos: [CENTER_ROW, CENTER_COL],
    instruction: "Invalid Jumps",
    detail: "Try jumping to the flashing spot northeast. The sequence bar turns red because there's no stone to jump over.",
    cursorStart: [CENTER_ROW, CENTER_COL],
    expectedAction: { type: 'invalid-jump' },
    highlightPos: [8, 9],  // Invalid northeast spot
  },

  // ===== HALTING MID-JUMP =====
  {
    id: 'halt-intro',
    board: [
      { pos: [CENTER_ROW - 1, CENTER_COL], type: TILE_TYPE.PHUTBALL_MAN },
      { pos: [CENTER_ROW - 3, CENTER_COL], type: TILE_TYPE.PHUTBALL_MAN },
      { pos: [CENTER_ROW - 5, CENTER_COL], type: TILE_TYPE.PHUTBALL_MAN },
    ],
    phutballPos: [CENTER_ROW, CENTER_COL],
    instruction: "Halting Early",
    detail: "You don't have to complete every possible jump. Sometimes stopping early is strategically correct. There's no \"pass\" in Phutball: you must place a stone or make at least one jump.",
    expectedAction: { type: 'continue' },
  },
  {
    id: 'halt-first-jump',
    board: [
      { pos: [CENTER_ROW - 1, CENTER_COL], type: TILE_TYPE.PHUTBALL_MAN },
      { pos: [CENTER_ROW - 3, CENTER_COL], type: TILE_TYPE.PHUTBALL_MAN },
      { pos: [CENTER_ROW - 5, CENTER_COL], type: TILE_TYPE.PHUTBALL_MAN },
    ],
    phutballPos: [CENTER_ROW, CENTER_COL],
    instruction: "One Jump",
    detail: (mobile) => mobile
      ? "Make just one jump, then stop. Tap the first landing and tap Jump."
      : "Make just one jump, then stop. Move to the first landing and press J.",
    cursorStart: [CENTER_ROW, CENTER_COL],
    expectedAction: { type: 'jump', pos: [CENTER_ROW - 2, CENTER_COL] },
    highlightPos: [CENTER_ROW - 2, CENTER_COL],
  },
  {
    id: 'halt-confirm-early',
    board: [
      { pos: [CENTER_ROW - 1, CENTER_COL], type: TILE_TYPE.PHUTBALL_MAN },
      { pos: [CENTER_ROW - 3, CENTER_COL], type: TILE_TYPE.PHUTBALL_MAN },
      { pos: [CENTER_ROW - 5, CENTER_COL], type: TILE_TYPE.PHUTBALL_MAN },
    ],
    phutballPos: [CENTER_ROW, CENTER_COL],
    instruction: "Stop Here",
    detail: (mobile) => mobile
      ? "Tap Confirm now to halt the sequence early. You could continue, but you're choosing not to. The remaining stones stay on the board."
      : "Press ENTER now to halt the sequence early. You could continue, but you're choosing not to. The remaining stones stay on the board.",
    cursorStart: [CENTER_ROW - 2, CENTER_COL],
    stagedJump: { path: [[CENTER_ROW - 2, CENTER_COL]], startPos: [CENTER_ROW, CENTER_COL] },
    expectedAction: { type: 'confirm' },
  },

  // ===== LOOPY JUMP SEQUENCE =====
  // Triangle pattern: stones at (9,7), (8,8), (9,8) form a valid loop
  // Jump path: (10,7) → (8,7) → (8,9) → (10,7)
  {
    id: 'loop-intro',
    board: [
      { pos: [CENTER_ROW - 1, CENTER_COL], type: TILE_TYPE.PHUTBALL_MAN },     // (9, 7)
      { pos: [CENTER_ROW - 2, CENTER_COL + 1], type: TILE_TYPE.PHUTBALL_MAN }, // (8, 8)
      { pos: [CENTER_ROW - 1, CENTER_COL + 1], type: TILE_TYPE.PHUTBALL_MAN }, // (9, 8)
    ],
    phutballPos: [CENTER_ROW, CENTER_COL],
    instruction: "The Futile Loop",
    detail: "Here's something peculiar: a jump sequence can consume pieces and return the ball to its starting position. This clears the board but gains no ground. You'll jump in a complete triangle.",
    expectedAction: { type: 'continue' },
  },
  {
    id: 'loop-jump-sequence',
    board: [
      { pos: [CENTER_ROW - 1, CENTER_COL], type: TILE_TYPE.PHUTBALL_MAN },
      { pos: [CENTER_ROW - 2, CENTER_COL + 1], type: TILE_TYPE.PHUTBALL_MAN },
      { pos: [CENTER_ROW - 1, CENTER_COL + 1], type: TILE_TYPE.PHUTBALL_MAN },
    ],
    phutballPos: [CENTER_ROW, CENTER_COL],
    instruction: "Complete the Triangle",
    detail: (mobile) => mobile
      ? "Stage three jumps: north, then east, then southwest back to start. Follow the pulse and tap Jump."
      : "Stage three jumps: north, then east, then southwest back to start. Follow the pulse and press J.",
    cursorStart: [CENTER_ROW, CENTER_COL],
    expectedAction: { type: 'jump', pos: [CENTER_ROW, CENTER_COL] },
    highlightSequence: [[CENTER_ROW - 2, CENTER_COL], [CENTER_ROW - 2, CENTER_COL + 2], [CENTER_ROW, CENTER_COL]],
  },
  {
    id: 'loop-confirm',
    board: [
      { pos: [CENTER_ROW - 1, CENTER_COL], type: TILE_TYPE.PHUTBALL_MAN },
      { pos: [CENTER_ROW - 2, CENTER_COL + 1], type: TILE_TYPE.PHUTBALL_MAN },
      { pos: [CENTER_ROW - 1, CENTER_COL + 1], type: TILE_TYPE.PHUTBALL_MAN },
    ],
    phutballPos: [CENTER_ROW, CENTER_COL],
    instruction: "Complete the Loop",
    detail: (mobile) => mobile
      ? "Tap Confirm. All three stones vanish, but the ball returns to where it started. Sometimes clearing the board is the point."
      : "Press ENTER. All three stones vanish, but the ball returns to where it started. Sometimes clearing the board is the point.",
    cursorStart: [CENTER_ROW, CENTER_COL],
    stagedJump: { path: [[CENTER_ROW - 2, CENTER_COL], [CENTER_ROW - 2, CENTER_COL + 2], [CENTER_ROW, CENTER_COL]], startPos: [CENTER_ROW, CENTER_COL] },
    expectedAction: { type: 'confirm' },
  },

  // ===== FIVE-FOLD RETURN =====
  // Complex pattern that visits center 5 times, demonstrating 8 doors concept
  {
    id: 'fivefold-intro',
    board: [
      { pos: [9, 7], type: TILE_TYPE.PHUTBALL_MAN },   // N
      { pos: [11, 7], type: TILE_TYPE.PHUTBALL_MAN },  // S
      { pos: [9, 8], type: TILE_TYPE.PHUTBALL_MAN },   // NE
      { pos: [11, 6], type: TILE_TYPE.PHUTBALL_MAN },  // SW
      { pos: [10, 8], type: TILE_TYPE.PHUTBALL_MAN },  // E
      { pos: [10, 6], type: TILE_TYPE.PHUTBALL_MAN },  // W
      { pos: [11, 8], type: TILE_TYPE.PHUTBALL_MAN },  // SE
      { pos: [9, 6], type: TILE_TYPE.PHUTBALL_MAN },   // NW
      { pos: [8, 8], type: TILE_TYPE.PHUTBALL_MAN },   // NNE arm
      { pos: [12, 6], type: TILE_TYPE.PHUTBALL_MAN },  // SSW arm (asymmetric stub)
      { pos: [9, 5], type: TILE_TYPE.PHUTBALL_MAN },   // NWW arm
      { pos: [11, 9], type: TILE_TYPE.PHUTBALL_MAN },  // SEE arm
      { pos: [7, 9], type: TILE_TYPE.PHUTBALL_MAN },   // NE extension
    ],
    phutballPos: [CENTER_ROW, CENTER_COL],
    instruction: "The Visit Limit",
    detail: "With eight doors and careful arrangement, the ball can visit the same position up to five times in one sequence: once at the start, plus four returns. Each return uses a different pair of doors.",
    expectedAction: { type: 'continue' },
  },
  {
    id: 'fivefold-sequence',
    board: [
      { pos: [9, 7], type: TILE_TYPE.PHUTBALL_MAN },
      { pos: [11, 7], type: TILE_TYPE.PHUTBALL_MAN },
      { pos: [9, 8], type: TILE_TYPE.PHUTBALL_MAN },
      { pos: [11, 6], type: TILE_TYPE.PHUTBALL_MAN },
      { pos: [10, 8], type: TILE_TYPE.PHUTBALL_MAN },
      { pos: [10, 6], type: TILE_TYPE.PHUTBALL_MAN },
      { pos: [11, 8], type: TILE_TYPE.PHUTBALL_MAN },
      { pos: [9, 6], type: TILE_TYPE.PHUTBALL_MAN },
      { pos: [8, 8], type: TILE_TYPE.PHUTBALL_MAN },
      { pos: [12, 6], type: TILE_TYPE.PHUTBALL_MAN },
      { pos: [9, 5], type: TILE_TYPE.PHUTBALL_MAN },
      { pos: [11, 9], type: TILE_TYPE.PHUTBALL_MAN },
      { pos: [7, 9], type: TILE_TYPE.PHUTBALL_MAN },
    ],
    phutballPos: [CENTER_ROW, CENTER_COL],
    instruction: "Execute the Pattern",
    detail: (mobile) => mobile
      ? "Stage all 12 jumps. The ball returns to center on jumps 3, 6, 9, and 12. Follow the star and tap Jump."
      : "Stage all 12 jumps. The ball returns to center on jumps 3, 6, 9, and 12. Follow the star and press J.",
    cursorStart: [CENTER_ROW, CENTER_COL],
    expectedAction: { type: 'jump', pos: [CENTER_ROW, CENTER_COL] },
    highlightSequence: [
      [8, 7], [8, 9], [10, 7],        // N, NE, back to center (1st return)
      [12, 7], [12, 5], [10, 7],      // S, SW, back to center (2nd return)
      [12, 9], [10, 9], [10, 7],      // SE, E, back to center (3rd return)
      [8, 5], [10, 5], [10, 7],       // NW, W, back to center (4th return)
    ],
  },
  {
    id: 'fivefold-confirm',
    board: [
      { pos: [9, 7], type: TILE_TYPE.PHUTBALL_MAN },
      { pos: [11, 7], type: TILE_TYPE.PHUTBALL_MAN },
      { pos: [9, 8], type: TILE_TYPE.PHUTBALL_MAN },
      { pos: [11, 6], type: TILE_TYPE.PHUTBALL_MAN },
      { pos: [10, 8], type: TILE_TYPE.PHUTBALL_MAN },
      { pos: [10, 6], type: TILE_TYPE.PHUTBALL_MAN },
      { pos: [11, 8], type: TILE_TYPE.PHUTBALL_MAN },
      { pos: [9, 6], type: TILE_TYPE.PHUTBALL_MAN },
      { pos: [8, 8], type: TILE_TYPE.PHUTBALL_MAN },
      { pos: [12, 6], type: TILE_TYPE.PHUTBALL_MAN },
      { pos: [9, 5], type: TILE_TYPE.PHUTBALL_MAN },
      { pos: [11, 9], type: TILE_TYPE.PHUTBALL_MAN },
      { pos: [7, 9], type: TILE_TYPE.PHUTBALL_MAN },
    ],
    phutballPos: [CENTER_ROW, CENTER_COL],
    instruction: "Complete the Pattern",
    detail: (mobile) => mobile
      ? "Tap Confirm. Twelve jumps, twelve stones consumed, and the ball ends where it began for five total visits."
      : "Press ENTER. Twelve jumps, twelve stones consumed, and the ball ends where it began for five total visits.",
    cursorStart: [CENTER_ROW, CENTER_COL],
    stagedJump: {
      path: [[8, 7], [8, 9], [10, 7], [12, 7], [12, 5], [10, 7], [12, 9], [10, 9], [10, 7], [8, 5], [10, 5], [10, 7]],
      startPos: [CENTER_ROW, CENTER_COL]
    },
    expectedAction: { type: 'confirm' },
  },

  // ===== ENDZONE ESCAPE SEQUENCE (efficient sparse layout) =====
  // Smart players use gaps between stones for efficiency: 1 stone = 2 squares traveled
  // Path: (10,7) → (12,5) → (14,3) → (16,3) → (18,3) → (20,3) → (18,5) → (16,7) → (14,9) → (12,11) → (10,13)
  // Total: 10 stones (vs 17 with contiguous blocks)
  {
    id: 'own-goal-intro',
    board: [
      // SW jump stones (with gap at 12,5)
      { pos: [11, 6], type: TILE_TYPE.PHUTBALL_MAN },
      { pos: [13, 4], type: TILE_TYPE.PHUTBALL_MAN },
      // S jump stones (with gaps)
      { pos: [15, 3], type: TILE_TYPE.PHUTBALL_MAN },
      { pos: [17, 3], type: TILE_TYPE.PHUTBALL_MAN },
      { pos: [19, 3], type: TILE_TYPE.PHUTBALL_MAN },
      // NE escape stones (with gaps)
      { pos: [19, 4], type: TILE_TYPE.PHUTBALL_MAN },
      { pos: [17, 6], type: TILE_TYPE.PHUTBALL_MAN },
      { pos: [15, 8], type: TILE_TYPE.PHUTBALL_MAN },
      { pos: [13, 10], type: TILE_TYPE.PHUTBALL_MAN },
      { pos: [11, 12], type: TILE_TYPE.PHUTBALL_MAN },
    ],
    phutballPos: [CENTER_ROW, CENTER_COL],
    instruction: "Danger: Own Goals",
    detail: "If your jump ends in your own endzone (red for P1), you lose. But you can pass through mid-sequence. Jump southwest twice.",
    cursorStart: [CENTER_ROW, CENTER_COL],
    expectedAction: { type: 'jump', pos: [14, 3] },
    highlightSequence: [[12, 5], [14, 3]],  // SW twice
  },
  {
    id: 'own-goal-into-endzone',
    board: [
      { pos: [11, 6], type: TILE_TYPE.PHUTBALL_MAN },
      { pos: [13, 4], type: TILE_TYPE.PHUTBALL_MAN },
      { pos: [15, 3], type: TILE_TYPE.PHUTBALL_MAN },
      { pos: [17, 3], type: TILE_TYPE.PHUTBALL_MAN },
      { pos: [19, 3], type: TILE_TYPE.PHUTBALL_MAN },
      { pos: [19, 4], type: TILE_TYPE.PHUTBALL_MAN },
      { pos: [17, 6], type: TILE_TYPE.PHUTBALL_MAN },
      { pos: [15, 8], type: TILE_TYPE.PHUTBALL_MAN },
      { pos: [13, 10], type: TILE_TYPE.PHUTBALL_MAN },
      { pos: [11, 12], type: TILE_TYPE.PHUTBALL_MAN },
    ],
    phutballPos: [CENTER_ROW, CENTER_COL],
    instruction: "Into the Fire",
    detail: "Jump south three times into the endzone. Don't continue jumping and don't commit the full sequence just yet.",
    cursorStart: [14, 3],
    stagedJump: { path: [[12, 5], [14, 3]], startPos: [CENTER_ROW, CENTER_COL] },
    expectedAction: { type: 'jump', pos: [20, 3] },
    highlightSequence: [[16, 3], [18, 3], [20, 3]],  // Three jumps south
  },
  {
    id: 'own-goal-escape',
    board: [
      { pos: [11, 6], type: TILE_TYPE.PHUTBALL_MAN },
      { pos: [13, 4], type: TILE_TYPE.PHUTBALL_MAN },
      { pos: [15, 3], type: TILE_TYPE.PHUTBALL_MAN },
      { pos: [17, 3], type: TILE_TYPE.PHUTBALL_MAN },
      { pos: [19, 3], type: TILE_TYPE.PHUTBALL_MAN },
      { pos: [19, 4], type: TILE_TYPE.PHUTBALL_MAN },
      { pos: [17, 6], type: TILE_TYPE.PHUTBALL_MAN },
      { pos: [15, 8], type: TILE_TYPE.PHUTBALL_MAN },
      { pos: [13, 10], type: TILE_TYPE.PHUTBALL_MAN },
      { pos: [11, 12], type: TILE_TYPE.PHUTBALL_MAN },
    ],
    phutballPos: [CENTER_ROW, CENTER_COL],
    instruction: "Escape!",
    detail: "You're in the endzone! Jump northeast five times back to center row.",
    cursorStart: [20, 3],
    stagedJump: { path: [[12, 5], [14, 3], [16, 3], [18, 3], [20, 3]], startPos: [CENTER_ROW, CENTER_COL] },
    expectedAction: { type: 'jump', pos: [10, 13] },
    highlightSequence: [[18, 5], [16, 7], [14, 9], [12, 11], [10, 13]],  // Five jumps NE
  },
  {
    id: 'own-goal-confirm',
    board: [
      { pos: [11, 6], type: TILE_TYPE.PHUTBALL_MAN },
      { pos: [13, 4], type: TILE_TYPE.PHUTBALL_MAN },
      { pos: [15, 3], type: TILE_TYPE.PHUTBALL_MAN },
      { pos: [17, 3], type: TILE_TYPE.PHUTBALL_MAN },
      { pos: [19, 3], type: TILE_TYPE.PHUTBALL_MAN },
      { pos: [19, 4], type: TILE_TYPE.PHUTBALL_MAN },
      { pos: [17, 6], type: TILE_TYPE.PHUTBALL_MAN },
      { pos: [15, 8], type: TILE_TYPE.PHUTBALL_MAN },
      { pos: [13, 10], type: TILE_TYPE.PHUTBALL_MAN },
      { pos: [11, 12], type: TILE_TYPE.PHUTBALL_MAN },
    ],
    phutballPos: [CENTER_ROW, CENTER_COL],
    instruction: "Safe!",
    detail: (mobile) => mobile
      ? "Tap Confirm. You passed through the endzone and escaped. The endzone is only deadly if you end there."
      : "Press ENTER. You passed through the endzone and escaped. The endzone is only deadly if you end there.",
    cursorStart: [10, 13],
    stagedJump: { path: [[12, 5], [14, 3], [16, 3], [18, 3], [20, 3], [18, 5], [16, 7], [14, 9], [12, 11], [10, 13]], startPos: [CENTER_ROW, CENTER_COL] },
    expectedAction: { type: 'confirm' },
  },

  // ===== TACKLING =====
  // Opponent has a diagonal attack: SW then S, S, S deep into your territory
  // Tackle: Place at (10,7) - extends their SW jump, breaking the whole chain
  {
    id: 'tackle-intro',
    board: [
      { pos: [CENTER_ROW - 1, CENTER_COL + 1], type: TILE_TYPE.PHUTBALL_MAN },  // (9,8) - first jump SW
      { pos: [CENTER_ROW + 1, CENTER_COL], type: TILE_TYPE.PHUTBALL_MAN },      // (11,7) - chain continues S
      { pos: [CENTER_ROW + 3, CENTER_COL], type: TILE_TYPE.PHUTBALL_MAN },      // (13,7)
      { pos: [CENTER_ROW + 5, CENTER_COL], type: TILE_TYPE.PHUTBALL_MAN },      // (15,7)
      { pos: [CENTER_ROW + 7, CENTER_COL], type: TILE_TYPE.PHUTBALL_MAN },      // (17,7)
      { pos: [CENTER_ROW, CENTER_COL + 1], type: TILE_TYPE.PHUTBALL_MAN },      // (10,8) - escape route
    ],
    phutballPos: [CENTER_ROW - 2, CENTER_COL + 2],  // Ball at (8,9)
    instruction: "Tackling",
    detail: "Your opponent has built an attack deep into your territory: SW then a long path south toward your endzone. You can throw a wrench in their chain with a tackle.",
    expectedAction: { type: 'continue' },
  },
  {
    id: 'tackle-place',
    board: [
      { pos: [CENTER_ROW - 1, CENTER_COL + 1], type: TILE_TYPE.PHUTBALL_MAN },
      { pos: [CENTER_ROW + 1, CENTER_COL], type: TILE_TYPE.PHUTBALL_MAN },
      { pos: [CENTER_ROW + 3, CENTER_COL], type: TILE_TYPE.PHUTBALL_MAN },
      { pos: [CENTER_ROW + 5, CENTER_COL], type: TILE_TYPE.PHUTBALL_MAN },
      { pos: [CENTER_ROW + 7, CENTER_COL], type: TILE_TYPE.PHUTBALL_MAN },
      { pos: [CENTER_ROW, CENTER_COL + 1], type: TILE_TYPE.PHUTBALL_MAN },      // (10,8) - escape route
    ],
    phutballPos: [CENTER_ROW - 2, CENTER_COL + 2],
    instruction: "Throw the Wrench",
    detail: "Place a stone at the beacon on the turn point. This extends their SW jump so they overshoot their initial path towards your endzone.",
    cursorStart: [CENTER_ROW, CENTER_COL],  // (10,7) - the turn point
    expectedAction: { type: 'place', pos: [CENTER_ROW, CENTER_COL] },
    highlightPos: [CENTER_ROW, CENTER_COL],
  },
  {
    id: 'tackle-confirm',
    board: [
      { pos: [CENTER_ROW - 1, CENTER_COL + 1], type: TILE_TYPE.PHUTBALL_MAN },
      { pos: [CENTER_ROW + 1, CENTER_COL], type: TILE_TYPE.PHUTBALL_MAN },
      { pos: [CENTER_ROW + 3, CENTER_COL], type: TILE_TYPE.PHUTBALL_MAN },
      { pos: [CENTER_ROW + 5, CENTER_COL], type: TILE_TYPE.PHUTBALL_MAN },
      { pos: [CENTER_ROW + 7, CENTER_COL], type: TILE_TYPE.PHUTBALL_MAN },
      { pos: [CENTER_ROW, CENTER_COL + 1], type: TILE_TYPE.PHUTBALL_MAN },
    ],
    phutballPos: [CENTER_ROW - 2, CENTER_COL + 2],
    instruction: "Confirm Tackle",
    detail: (mobile) => mobile
      ? "Tap Confirm to commit. They cannot use the chain below anymore without spending turns to realign."
      : "Press ENTER to commit. They cannot use the chain below anymore without spending turns to realign.",
    cursorStart: [CENTER_ROW, CENTER_COL],
    stagedMove: { type: 'placement', path: [[CENTER_ROW, CENTER_COL]] },
    expectedAction: { type: 'confirm' },
  },
  {
    id: 'tackle-result',
    board: [
      { pos: [CENTER_ROW - 1, CENTER_COL + 1], type: TILE_TYPE.PHUTBALL_MAN },  // (9,8)
      { pos: [CENTER_ROW, CENTER_COL], type: TILE_TYPE.PHUTBALL_MAN },          // (10,7) - your tackle
      { pos: [CENTER_ROW + 1, CENTER_COL], type: TILE_TYPE.PHUTBALL_MAN },      // (11,7)
      { pos: [CENTER_ROW + 3, CENTER_COL], type: TILE_TYPE.PHUTBALL_MAN },      // (13,7)
      { pos: [CENTER_ROW + 5, CENTER_COL], type: TILE_TYPE.PHUTBALL_MAN },      // (15,7)
      { pos: [CENTER_ROW + 7, CENTER_COL], type: TILE_TYPE.PHUTBALL_MAN },      // (17,7)
      { pos: [CENTER_ROW, CENTER_COL + 1], type: TILE_TYPE.PHUTBALL_MAN },      // (10,8) - escape route
    ],
    phutballPos: [CENTER_ROW - 2, CENTER_COL + 2],
    instruction: "Chain Broken",
    detail: "Jump SW to see the result. You could continue jumping toward their goal for partial progress, but disrupting their line and continuing to build is often the stronger play.",
    cursorStart: [CENTER_ROW - 2, CENTER_COL + 2],
    expectedAction: { type: 'jump', pos: [CENTER_ROW + 1, CENTER_COL - 1] },  // (11,6)
    highlightPos: [CENTER_ROW + 1, CENTER_COL - 1],
  },

  // ===== FINAL WISDOM =====
  {
    id: 'dedication',
    board: [],
    phutballPos: [CENTER_ROW - 5, CENTER_COL],
    instruction: "Dedication",
    detail: "This interface is dedicated to John Conway, who invented this game deliberately to resist analysis (I wish I could have watched him play in person before the world went into lockdown and he passed). To Elwyn Berlekamp and Richard Guy, his co-authors on Winning Ways. To Martin Gardner, whose Mathematical Games column put so much of Conway's other work in front of the rest of us. And to my brother, my lifelong sparring partner in all things, including Phutball.\n\nOne last bit of wisdom from Conway: try not to jump until you really have to, and then only as far as you really must. Now go find out what Phutball has been hiding from you.",
    expectedAction: { type: 'finish' },
  },
];

// Evaluate if a jump path is valid
const evaluateJumpSequence = (board, startPos, path) => {
  const jumpedPieces = [];
  // Initialize with starting position (so loops show count of 2)
  const startKey = `${startPos[0]},${startPos[1]}`;
  const usageCounts = { [startKey]: 1 };
  // Track men consumed by prior jumps in the sequence (so they can't be re-jumped)
  const consumed = new Set();
  let currentPos = startPos;
  let invalidIndex = -1;

  const isManAt = (r, c) => {
    if (consumed.has(`${r},${c}`)) return false;
    return board[r]?.[c] === TILE_TYPE.PHUTBALL_MAN;
  };

  for (let i = 0; i < path.length; i++) {
    const target = path[i];
    const dr = target[0] - currentPos[0];
    const dc = target[1] - currentPos[1];

    const absDr = Math.abs(dr);
    const absDc = Math.abs(dc);
    if (absDr === 0 && absDc === 0) {
      invalidIndex = i;
      break;
    }
    if (absDr !== absDc && absDr !== 0 && absDc !== 0) {
      invalidIndex = i;
      break;
    }

    const dist = Math.max(absDr, absDc);
    if (dist < 2) {
      invalidIndex = i;
      break;
    }

    const stepR = dr === 0 ? 0 : dr / absDr;
    const stepC = dc === 0 ? 0 : dc / absDc;

    // All intermediate cells must be men (matches real engine rule)
    const hopJumped = [];
    let pathValid = true;
    for (let j = 1; j < dist; j++) {
      const checkR = currentPos[0] + stepR * j;
      const checkC = currentPos[1] + stepC * j;
      if (!isManAt(checkR, checkC)) {
        pathValid = false;
        break;
      }
      hopJumped.push([checkR, checkC]);
    }

    if (!pathValid || hopJumped.length === 0) {
      invalidIndex = i;
      break;
    }

    // Landing square must not be a man
    if (isManAt(target[0], target[1])) {
      invalidIndex = i;
      break;
    }

    // Record jumped men and mark them consumed for subsequent jumps
    for (const [jr, jc] of hopJumped) {
      jumpedPieces.push([jr, jc]);
      consumed.add(`${jr},${jc}`);
    }

    const key = `${target[0]},${target[1]}`;
    usageCounts[key] = (usageCounts[key] || 0) + 1;
    currentPos = target;
  }

  return {
    jumpedPieces,
    usageCounts,
    invalidIndex,
    isValid: invalidIndex === -1 && path.length > 0,
  };
};

// Helper to generate hint text for current step
const getHintText = (expectedAction, mobile) => {
  switch (expectedAction.type) {
    case 'continue':
      return <span>Press <kbd>]</kbd> or click the next button at the top to continue</span>;
    case 'finish':
      return <span>Press <kbd>]</kbd> or click the next button at the top to finish</span>;
    case 'move-cursor':
      return mobile
        ? <span>Tap the guiding star</span>
        : <span>Use <kbd>W</kbd><kbd>A</kbd><kbd>S</kbd><kbd>D</kbd> to move cursor</span>;
    case 'click':
      return <span>{mobile ? 'Tap' : 'Click'} on the board to move cursor</span>;
    case 'place':
      return mobile
        ? <span>Tap <kbd>Place</kbd></span>
        : <span>Press <kbd>P</kbd> to place a stone</span>;
    case 'jump':
      return mobile
        ? <span>Tap <kbd>Jump</kbd></span>
        : <span>Press <kbd>J</kbd> to add jump</span>;
    case 'invalid-jump':
      return mobile
        ? <span>Tap <kbd>Jump</kbd> to try the jump</span>
        : <span>Press <kbd>J</kbd> to try the jump</span>;
    case 'invalid-place':
      return mobile
        ? <span>Move to the guiding star and tap <kbd>Place</kbd></span>
        : <span>Move to the guiding star and press <kbd>P</kbd></span>;
    case 'undo':
      return mobile
        ? <span>Tap <kbd>Undo</kbd></span>
        : <span>Press <kbd>BACKSPACE</kbd> to undo</span>;
    case 'confirm':
      return mobile
        ? <span>Tap <kbd>Confirm</kbd></span>
        : <span>Press <kbd>ENTER</kbd> to confirm</span>;
    case 'focus-shift':
      if (expectedAction.target === 'history') {
        return <span>Press <kbd>SHIFT</kbd>+<kbd>D</kbd> to focus history panel</span>;
      }
      return <span>Press <kbd>SHIFT</kbd>+<kbd>A</kbd> to focus board</span>;
    case 'key':
      if (mobile && (expectedAction.key === 'f' || expectedAction.key === 'c')) {
        return <span>Tap the <kbd>⌖</kbd> button</span>;
      }
      return <span>Press <kbd>{expectedAction.key.toUpperCase()}</kbd></span>;
    default:
      return null;
  }
};

// Determine which controls to highlight based on current expected action
const getActiveControls = (expectedAction, needsUndo, stepDone, { requiresTransform, boardTransformed } = {}) => {
  if (stepDone) return ['next'];
  if (needsUndo) return ['undo'];
  if (!expectedAction) return [];
  switch (expectedAction.type) {
    case 'move-cursor':
      return ['wasd'];
    case 'click':
      return ['click'];
    case 'place':
    case 'invalid-place':
      return ['wasd', 'place'];
    case 'jump':
    case 'invalid-jump':
      return ['jump'];
    case 'confirm':
      return ['confirm'];
    case 'undo':
      return ['undo'];
    case 'continue':
    case 'finish':
      return ['next'];
    case 'key':
      if (requiresTransform && !boardTransformed) return ['pan', 'zoom'];
      if (expectedAction.key === 'f') return ['fit'];
      if (expectedAction.key === 'c') return ['center'];
      return [];
    default:
      return [];
  }
};

// Compute preview animation data for minimap based on tutorial step
const computeTutorialPreview = (step) => {
  if (!step?.expectedAction) return { type: 'none' };
  const action = step.expectedAction;
  switch (action.type) {
    case 'move-cursor':
      return {
        type: 'cursor-move',
        from: step.cursorStart || step.phutballPos,
        to: action.target,
      };
    case 'click':
      return {
        type: 'cursor-move',
        from: step.cursorStart || step.phutballPos,
        to: action.target,
      };
    case 'place':
      return { type: 'place', to: action.pos };
    case 'invalid-place':
      return { type: 'place', to: step.highlightPos || [action.targetRow, CENTER_COL] };
    case 'jump': {
      if (step.highlightPos && !step.highlightSequence && !step.stagedJump) {
        // Single target highlight - show as a placement ghost rather than a jump line
        return { type: 'place', to: step.highlightPos };
      }
      const jumpPath = step.highlightSequence || (step.highlightPos ? [step.highlightPos] : [action.pos]);
      let ballStart = step.phutballPos;
      if (step.stagedJump?.path?.length > 0) {
        ballStart = step.stagedJump.path[step.stagedJump.path.length - 1];
      }
      return { type: 'jump', ballStart, jumpPath };
    }
    case 'invalid-jump':
      return {
        type: 'jump',
        ballStart: step.phutballPos,
        jumpPath: step.highlightPos ? [step.highlightPos] : [],
      };
    case 'undo':
      return { type: 'button-hint', label: '⌫', ballPos: step.phutballPos };
    case 'confirm': {
      if (step.stagedJump?.path?.length > 0) {
        return { type: 'jump', ballStart: step.phutballPos, jumpPath: step.stagedJump.path, label: 'ENTER' };
      }
      return { type: 'button-hint', label: 'ENTER', ballPos: step.phutballPos };
    }
    case 'key':
      return { type: 'button-hint', label: action.key.toUpperCase() };
    case 'continue':
      if (step.id && step.id.includes('goal')) {
        return { type: 'highlight-zones' };
      }
      return { type: 'none' };
    case 'finish':
      return { type: 'none' };
    default:
      return { type: 'none' };
  }
};

// Progressive unlock map - step indices where controls become available
const UNLOCK_MAP = {
  genesis: { cursor: 1, click: 1, place: 1, confirm: 4, jump: 3 },
  full:    { cursor: 2, click: 2, place: 4, confirm: 5, jump: 8 },
};

const Tutorial = ({ onExit, tutorialType = 'full' }) => {
  const allSteps = tutorialType === 'genesis' ? GENESIS_STEPS : TUTORIAL_STEPS;
  const unlocks = UNLOCK_MAP[tutorialType] || UNLOCK_MAP.full;
  const [stepIndex, setStepIndex] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem('phutball_tutorial') || '{}');
      if (saved.tutorialType === tutorialType && typeof saved.stepIndex === 'number') {
        return Math.min(saved.stepIndex, allSteps.length - 1);
      }
    } catch (e) { /* ignore */ }
    return 0;
  });
  const [maxStepReached, setMaxStepReached] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem('phutball_tutorial') || '{}');
      if (saved.tutorialType === tutorialType && typeof saved.maxStepReached === 'number') {
        return Math.min(saved.maxStepReached, allSteps.length - 1);
      }
    } catch (e) { /* ignore */ }
    return 0;
  });
  const [cursorPos, setCursorPos] = useState(null);
  const [hoverPos, setHoverPos] = useState(null);
  const [stagedMove, setStagedMove] = useState({ type: 'none', path: [] });
  const [jumpPreview, setJumpPreview] = useState({ jumpedPieces: [], usageCounts: {}, invalidIndex: -1, isValid: false });
  const [keyFocus, setKeyFocus] = useState('board');
  const [focusAnimating, setFocusAnimating] = useState(false);
  const [coordSystem, setCoordSystem] = useState('endzone');
  const [showMinimap, setShowMinimap] = useState(true);
  const [stepCompleted, setStepCompleted] = useState(false);
  const [logHistory, setLogHistory] = useState([]);
  const [boardTransformed, setBoardTransformed] = useState(false);
  const [showCompletion, setShowCompletion] = useState(false);
  const [showPauseMenu, setShowPauseMenu] = useState(false);
  const [overlaySelectedIndex, setOverlaySelectedIndex] = useState(0);
  const [invalidPlacement, setInvalidPlacement] = useState(null);
  const [tutorialCompleted, setTutorialCompleted] = useState(false);
  const [sequenceIndex, setSequenceIndex] = useState(0);
  const controlsRef = useRef(null);

  // Persist tutorial progress
  useEffect(() => {
    try {
      localStorage.setItem('phutball_tutorial', JSON.stringify({ tutorialType, stepIndex, maxStepReached }));
    } catch (e) { /* quota */ }
  }, [tutorialType, stepIndex, maxStepReached]);

  // Clear saved progress and exit
  const handleExit = useCallback(() => {
    // Telemetry: tutorial drop-off / completion funnel
    trackEvent('tutorial_progress', {
      tutorial_type: tutorialType,
      step_reached: stepIndex,
      max_step_reached: maxStepReached,
      total_steps: allSteps.length,
      completed: tutorialCompleted,
    });
    try { localStorage.removeItem('phutball_tutorial'); } catch (e) { /* ignore */ }
    onExit();
  }, [onExit, tutorialType, stepIndex, maxStepReached, allSteps.length, tutorialCompleted]);

  // Stacked/mobile layout state
  const [isStackedLayout, setIsStackedLayout] = useState(() => shouldUseStackedLayout());
  const steps = isStackedLayout ? allSteps.filter(s => !s.desktopOnly) : allSteps;
  const [activePanelIndex, setActivePanelIndex] = useState(0); // start on tutorial page
  const activePanelIndexRef = useRef(0);
  const swipeContainerRef = useRef(null);
  const wheelGestureStateRef = useRef({ accumX: 0, lockedUntil: 0 });
  const [swipeOffsetPct, setSwipeOffsetPct] = useState(0);
  const [panelFlash, setPanelFlash] = useState(null);
  const flashPanel = useCallback((idx) => { setPanelFlash(idx); setTimeout(() => setPanelFlash(null), 400); }, []);
  const [isPagerDragging, setIsPagerDragging] = useState(false);
  const [forceNoTransition, setForceNoTransition] = useState(false);

  const settlePagerOffset = useCallback(() => {
    setIsPagerDragging(false);
    setForceNoTransition(true);
    setSwipeOffsetPct(0);
  }, []);

  // --- Stacked layout effects ---
  useEffect(() => { activePanelIndexRef.current = activePanelIndex; }, [activePanelIndex]);

  useEffect(() => {
    if (!isStackedLayout) { settlePagerOffset(); }
  }, [isStackedLayout, settlePagerOffset]);

  useEffect(() => {
    if (!isStackedLayout) return;
    setSwipeOffsetPct(0);
    setIsPagerDragging(false);
  }, [activePanelIndex, isStackedLayout]);

  useEffect(() => {
    if (typeof window === 'undefined') return () => {};
    const handleResize = () => setIsStackedLayout(shouldUseStackedLayout());
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (!forceNoTransition) return undefined;
    const id = requestAnimationFrame(() => setForceNoTransition(false));
    return () => cancelAnimationFrame(id);
  }, [forceNoTransition]);

  // Pointer swipe handling
  useEffect(() => {
    if (!isStackedLayout) return () => {};
    const container = swipeContainerRef.current;
    if (!container) return () => {};
    let pointerId = null;
    let startX = 0;
    let startY = 0;
    let tracking = false;
    let requiredDistance = 0;

    const resetTracking = () => { pointerId = null; tracking = false; requiredDistance = 0; };

    const handlePointerDown = (event) => {
      if (!isStackedLayout || tracking) return;
      if (event.pointerType === 'mouse' && event.buttons !== 1) return;
      const boardLayout = event.target?.closest('.board-layout-container');
      if (boardLayout) return;
      pointerId = event.pointerId;
      startX = event.clientX;
      startY = event.clientY;
      requiredDistance = computeSwipeThreshold(container, POINTER_SWIPE_FRACTION);
      tracking = true;
      setIsPagerDragging(true);
      setSwipeOffsetPct(0);
    };

    const handlePointerMove = (event) => {
      if (!tracking || (pointerId != null && event.pointerId !== pointerId)) return;
      const dx = event.clientX - startX;
      const width = container.clientWidth || window.innerWidth || 1;
      let offsetPct = Math.max(-100, Math.min(100, (dx / width) * 100));
      const panelIdx = activePanelIndexRef.current;
      if (panelIdx === 0 && offsetPct > 0) offsetPct = 0;
      else if (panelIdx === TUTORIAL_PANEL_CONFIG.length - 1 && offsetPct < 0) offsetPct = 0;
      setSwipeOffsetPct(offsetPct);
    };

    const handlePointerUp = (event) => {
      if (!tracking || (pointerId != null && event.pointerId !== pointerId)) {
        resetTracking(); settlePagerOffset(); return;
      }
      const dx = event.clientX - startX;
      const dy = event.clientY - startY;
      const threshold = requiredDistance > 0 ? requiredDistance : computeSwipeThreshold(container, POINTER_SWIPE_FRACTION);
      if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) >= threshold) {
        const direction = dx < 0 ? 1 : -1;
        resetTracking();
        setActivePanelIndex(prev => clampTutorialPanelIndex(prev + direction));
        settlePagerOffset();
        return;
      }
      resetTracking(); settlePagerOffset();
    };

    const handlePointerCancel = () => { if (!tracking) return; resetTracking(); settlePagerOffset(); };

    container.addEventListener('pointerdown', handlePointerDown, { passive: true });
    container.addEventListener('pointermove', handlePointerMove, { passive: true });
    container.addEventListener('pointerup', handlePointerUp);
    container.addEventListener('pointercancel', handlePointerCancel);
    container.addEventListener('pointerleave', handlePointerCancel);
    return () => {
      container.removeEventListener('pointerdown', handlePointerDown);
      container.removeEventListener('pointermove', handlePointerMove);
      container.removeEventListener('pointerup', handlePointerUp);
      container.removeEventListener('pointercancel', handlePointerCancel);
      container.removeEventListener('pointerleave', handlePointerCancel);
    };
  }, [isStackedLayout, settlePagerOffset]);

  // Wheel swipe handling
  useEffect(() => {
    if (!isStackedLayout) return () => {};
    const container = swipeContainerRef.current;
    if (!container) return () => {};
    const handleWheel = (event) => {
      const indicatorTarget = event.target?.closest?.('.panel-indicator-bar');
      const isShift = event.shiftKey;
      const dominantHorizontal = Math.abs(event.deltaX) >= Math.abs(event.deltaY);
      if (!indicatorTarget && !isShift && !dominantHorizontal) return;
      const gestureState = wheelGestureStateRef.current;
      const now = Date.now();
      if (gestureState.lockedUntil && now < gestureState.lockedUntil) { event.preventDefault(); return; }
      const width = container.clientWidth || window.innerWidth || 1;
      const thresholdPx = computeSwipeThreshold(container, WHEEL_SWIPE_FRACTION);
      const scrollDelta = -event.deltaX;
      gestureState.accumX += scrollDelta;
      const panelIdx = activePanelIndexRef.current;
      if (panelIdx === 0 && gestureState.accumX > 0) gestureState.accumX = 0;
      else if (panelIdx === TUTORIAL_PANEL_CONFIG.length - 1 && gestureState.accumX < 0) gestureState.accumX = 0;
      let offsetPct = Math.max(-100, Math.min(100, (gestureState.accumX / width) * 100));
      if (panelIdx === 0 && offsetPct > 0) offsetPct = 0;
      else if (panelIdx === TUTORIAL_PANEL_CONFIG.length - 1 && offsetPct < 0) offsetPct = 0;
      setIsPagerDragging(true);
      setSwipeOffsetPct(offsetPct);
      if (Math.abs(gestureState.accumX) >= thresholdPx) {
        const direction = gestureState.accumX < 0 ? 1 : -1;
        gestureState.accumX = 0;
        gestureState.lockedUntil = now + WHEEL_COOLDOWN_MS;
        setActivePanelIndex(prev => clampTutorialPanelIndex(prev + direction));
        settlePagerOffset();
        event.preventDefault();
        return;
      }
      gestureState.lockedUntil = 0;
      event.preventDefault();
    };
    container.addEventListener('wheel', handleWheel, { passive: false });
    return () => {
      container.removeEventListener('wheel', handleWheel);
      const cleanupGestureState = wheelGestureStateRef.current;
      cleanupGestureState.lockedUntil = 0;
      cleanupGestureState.accumX = 0;
      settlePagerOffset();
    };
  }, [isStackedLayout, settlePagerOffset]);

  // Eight Doors animation state
  const [eightDoorsIndex, setEightDoorsIndex] = useState(0);
  const [eightDoorsPhase, setEightDoorsPhase] = useState('highlight'); // 'highlight', 'staged', 'executed'

  // Eight directions clockwise from N, with landing positions relative to center (10, 7)
  const EIGHT_DOORS_LANDINGS = [
    [CENTER_ROW - 2, CENTER_COL],     // N
    [CENTER_ROW - 2, CENTER_COL + 2], // NE
    [CENTER_ROW, CENTER_COL + 2],     // E
    [CENTER_ROW + 2, CENTER_COL + 2], // SE
    [CENTER_ROW + 2, CENTER_COL],     // S
    [CENTER_ROW + 2, CENTER_COL - 2], // SW
    [CENTER_ROW, CENTER_COL - 2],     // W
    [CENTER_ROW - 2, CENTER_COL - 2], // NW
  ];

  const rawStep = steps[stepIndex];

  // Support action sequences: steps with actionSequence use sequenceIndex
  // to progress through multiple actions on a single page
  const step = useMemo(() => {
    if (!rawStep.actionSequence) return rawStep;
    const idx = Math.min(sequenceIndex, rawStep.actionSequence.length - 1);
    const action = rawStep.actionSequence[idx];
    return {
      ...rawStep,
      // Clear per-action props so rawStep values don't leak across phases
      stagedMove: undefined,
      stagedJump: undefined,
      highlightPos: undefined,
      highlightSequence: undefined,
      // Apply action-specific overrides
      expectedAction: action.expectedAction,
      ...(action.stagedMove !== undefined && { stagedMove: action.stagedMove }),
      ...(action.stagedJump !== undefined && { stagedJump: action.stagedJump }),
      ...(action.highlightPos !== undefined && { highlightPos: action.highlightPos }),
      ...(action.highlightSequence !== undefined && { highlightSequence: action.highlightSequence }),
      ...(action.instruction !== undefined && { instruction: action.instruction }),
      ...(action.detail !== undefined && { detail: action.detail }),
    };
  }, [rawStep, sequenceIndex]);

  // Compute tutorial minimap preview for current step
  const tutorialPreview = useMemo(() => computeTutorialPreview(step), [step]);

  // Compute animated ball position for Eight Doors (needed before board building)
  const getAnimatedBallPos = () => {
    if (step.animatedDemo === 'eight-doors' && eightDoorsPhase === 'executed') {
      return EIGHT_DOORS_LANDINGS[eightDoorsIndex];
    }
    return null;
  };

  // Build board state
  // Handle confirmed placements and jumps
  let boardPieces = [...step.board];
  let ballPos = step.phutballPos;

  // Eight Doors animation - move ball during 'executed' phase
  const animatedBallPos = getAnimatedBallPos();
  if (animatedBallPos) {
    ballPos = animatedBallPos;
  } else if (step.expectedAction?.type === 'confirm' && stepCompleted) {
    if (step.stagedMove?.path) {
      // Placement confirmed - add the placed piece
      boardPieces = [
        ...boardPieces,
        ...step.stagedMove.path.map(pos => ({ pos, type: TILE_TYPE.PHUTBALL_MAN })),
      ];
    } else if (step.stagedJump?.path && step.stagedJump.path.length > 0) {
      // Jump confirmed - remove jumped pieces and move ball
      const tempBoard = setBoardPieces(createEmptyBoard(), [
        { pos: step.phutballPos, type: TILE_TYPE.PHUTBALL },
        ...step.board,
      ]);
      const { jumpedPieces } = evaluateJumpSequence(tempBoard, step.stagedJump.startPos, step.stagedJump.path);
      const jumpedSet = new Set(jumpedPieces.map(p => `${p[0]},${p[1]}`));
      boardPieces = boardPieces.filter(p => !jumpedSet.has(`${p.pos[0]},${p.pos[1]}`));
      ballPos = step.stagedJump.path[step.stagedJump.path.length - 1];
    }
  }

  const board = setBoardPieces(createEmptyBoard(), [
    { pos: ballPos, type: TILE_TYPE.PHUTBALL },
    ...boardPieces.map(p => ({ pos: p.pos, type: p.type })),
  ]);

  // Keep a ref to the current board for log breadcrumb snapshots
  const boardRef = useRef(board);
  boardRef.current = board;

  // Initialize cursor when step changes
  useEffect(() => {
    const currentStep = steps[stepIndex];
    const currentBoard = setBoardPieces(createEmptyBoard(), [
      { pos: currentStep.phutballPos, type: TILE_TYPE.PHUTBALL },
      ...currentStep.board,
    ]);

    // Reset step completion when step changes
    // 'continue' and 'finish' steps are always completable (no task required)
    const isContinueStep = currentStep.expectedAction?.type === 'continue' ||
                           currentStep.expectedAction?.type === 'finish';
    setStepCompleted(isContinueStep);

    // Reset action sequence index for multi-action steps
    setSequenceIndex(0);

    // Reset board transformation tracking
    setBoardTransformed(false);

    // Reset invalid placement indicator
    setInvalidPlacement(null);

    // Always reset focus to board when step changes
    setKeyFocus('board');

    if (currentStep.cursorStart) {
      setCursorPos(currentStep.cursorStart);
    } else {
      setCursorPos(null);
    }

    if (currentStep.stagedMove) {
      setStagedMove(currentStep.stagedMove);
      setJumpPreview({ jumpedPieces: [], usageCounts: {}, invalidIndex: -1, isValid: false });
    } else if (currentStep.stagedJump) {
      setStagedMove({ type: 'jump', ...currentStep.stagedJump });
      setJumpPreview(evaluateJumpSequence(currentBoard, currentStep.stagedJump.startPos, currentStep.stagedJump.path));
    } else {
      setStagedMove({ type: 'none', path: [] });
      setJumpPreview({ jumpedPieces: [], usageCounts: {}, invalidIndex: -1, isValid: false });
    }
  }, [stepIndex]);

  // Eight Doors animation loop
  useEffect(() => {
    if (step.animatedDemo !== 'eight-doors') {
      // Reset animation state when leaving the step
      setEightDoorsIndex(0);
      setEightDoorsPhase('highlight');
      return;
    }

    // Animation timing: highlight -> staged -> executed -> next direction
    const phaseDurations = {
      highlight: 400,  // Show highlight
      staged: 600,     // Show staged jump preview
      executed: 500,   // Show ball at landing
    };

    const timer = setTimeout(() => {
      if (eightDoorsPhase === 'highlight') {
        setEightDoorsPhase('staged');
      } else if (eightDoorsPhase === 'staged') {
        setEightDoorsPhase('executed');
      } else {
        // Move to next direction
        setEightDoorsIndex((prev) => (prev + 1) % 8);
        setEightDoorsPhase('highlight');
      }
    }, phaseDurations[eightDoorsPhase]);

    return () => clearTimeout(timer);
  }, [step.animatedDemo, eightDoorsIndex, eightDoorsPhase]);

  const advanceStep = useCallback(() => {
    const snapshot = tutorialBoardText(boardRef.current);
    const leavingInstruction = steps[stepIndex]?.instruction;
    if (stepIndex < steps.length - 1) {
      setLogHistory(h => {
        // Truncate to current step to prevent duplicates when going back and forth
        const trimmed = h.slice(0, stepIndex);
        return [...trimmed, {
          turnIndex: stepIndex + 1,
          player: 1,
          actions: [{
            moveDesc: leavingInstruction,
            boardText: snapshot,
            reasoning: null,
          }],
          turnReasoning: null,
        }];
      });
      setStepIndex(stepIndex + 1);
      setMaxStepReached(prev => Math.max(prev, stepIndex + 1));
    }
  }, [steps, stepIndex]);

  const goBackStep = useCallback(() => {
    setStepIndex(prev => {
      if (prev > 0) {
        return prev - 1;
      }
      return prev;
    });
  }, []);

  // Advance through action sequences or complete the step
  const completeCurrentAction = useCallback(() => {
    if (rawStep.actionSequence && sequenceIndex < rawStep.actionSequence.length - 1) {
      // More actions in this sequence - advance to the next one
      setSequenceIndex(sequenceIndex + 1);
      setStepCompleted(false);
    } else {
      // Final action or no sequence - complete the step
      setStepCompleted(true);
    }
  }, [rawStep, sequenceIndex]);

  // Toggle coordinate system - same as App.js
  const toggleCoordSystem = useCallback(() => {
    setCoordSystem(prev => prev === 'matrix' ? 'endzone' : 'matrix');
  }, []);

  // Progressive unlocking - controls become available after they're introduced
  const allowsCursor = stepIndex >= unlocks.cursor;
  const allowsPlace = stepIndex >= unlocks.place;
  const allowsConfirm = stepIndex >= unlocks.confirm;
  const allowsJump = stepIndex >= unlocks.jump;

  // Compute whether undo is possible (something staged to undo)
  const allowsUndo = (allowsJump && stagedMove.type === 'jump' && stagedMove.path.length > 0) ||
                     (allowsPlace && stagedMove.type === 'placement' && step.expectedAction?.type !== 'confirm') ||
                     (step.expectedAction?.type === 'undo' && stagedMove.type !== 'none');

  // Extracted action handlers - callable from both keyboard and buttons
  const handlePlace = useCallback(() => {
    const expected = step.expectedAction;
    if (expected.type === 'confirm' && stepCompleted) return;
    if (!cursorPos) return;

    // Check if placement is invalid (deep endzone rows or on the ball)
    const isDeepEndzone = cursorPos[0] === 0 || cursorPos[0] === ROWS - 1;
    const isOnBall = cursorPos[0] === ballPos[0] && cursorPos[1] === ballPos[1];
    const isInvalid = isDeepEndzone || isOnBall;

    if (isInvalid) {
      // Show invalid placement indicator - keep it staged so red piece renders
      setInvalidPlacement(cursorPos);
      setStagedMove({ type: 'placement', path: [cursorPos] });
      // Check if this completes an invalid-place step
      if (expected.type === 'invalid-place' && expected.targetRow !== undefined &&
          cursorPos[0] === expected.targetRow) {
        completeCurrentAction();
      }
    } else {
      // Valid placement
      setInvalidPlacement(null);
      setStagedMove({ type: 'placement', path: [cursorPos] });
      // Only mark complete if this step expects a place at this position
      if (expected.type === 'place' && expected.pos &&
          cursorPos[0] === expected.pos[0] && cursorPos[1] === expected.pos[1]) {
        completeCurrentAction();
      }
    }
  }, [step, cursorPos, stepCompleted, ballPos, completeCurrentAction]);

  const handleJump = useCallback(() => {
    const expected = step.expectedAction;
    if (expected.type === 'confirm' && stepCompleted) return;
    if (!cursorPos) return;

    const currentPath = stagedMove.type === 'jump' ? stagedMove.path : [];
    const startPos = stagedMove.type === 'jump' && stagedMove.startPos
      ? stagedMove.startPos
      : step.phutballPos;
    const newPath = [...currentPath, cursorPos];
    const preview = evaluateJumpSequence(board, startPos, newPath);
    setStagedMove({ type: 'jump', path: newPath, startPos });
    setJumpPreview(preview);
    // Only mark complete if the jump sequence is valid AND this step expects a jump at this position
    // (invalid-jump steps are handled separately — they require staging an invalid jump)
    const isValidSequence = preview.invalidIndex === -1;
    if (isValidSequence && expected.type === 'jump' && expected.pos &&
        cursorPos[0] === expected.pos[0] && cursorPos[1] === expected.pos[1]) {
      completeCurrentAction();
    }
  }, [step, cursorPos, stagedMove, stepCompleted, board, completeCurrentAction]);

  const handleConfirm = useCallback(() => {
    const expected = step.expectedAction;
    if (stagedMove.type === 'none' || expected.type !== 'confirm') return;
    // Don't confirm if there's an invalid jump staged
    if (jumpPreview?.invalidIndex >= 0) return;
    completeCurrentAction();
    // Clear staged move so piece shows as placed (not staged)
    setStagedMove({ type: 'none', path: [] });
    setJumpPreview({ jumpedPieces: [], usageCounts: {}, invalidIndex: -1, isValid: false });
  }, [step, stagedMove, jumpPreview, completeCurrentAction]);

  const handleUndo = useCallback(() => {
    const expected = step.expectedAction;
    // Undo last jump
    if (stagedMove.type === 'jump' && stagedMove.path.length > 0) {
      const newPath = stagedMove.path.slice(0, -1);
      setStagedMove({ ...stagedMove, path: newPath });
      if (newPath.length > 0) {
        setJumpPreview(evaluateJumpSequence(board, stagedMove.startPos, newPath));
      } else {
        setJumpPreview({ jumpedPieces: [], usageCounts: {}, invalidIndex: -1, isValid: false });
      }
      if (expected.type === 'undo') {
        // Only advance when the remaining path is fully valid (no invalid jumps left)
        const newPreview = newPath.length > 0
          ? evaluateJumpSequence(board, stagedMove.startPos, newPath)
          : { jumpedPieces: [], usageCounts: {}, invalidIndex: -1, isValid: false };
        if (newPreview.invalidIndex === -1) {
          completeCurrentAction();
        }
      } else if (rawStep.actionSequence) {
        // Undid back while in an action sequence - revert to the jump phase
        const jumpIdx = rawStep.actionSequence.findIndex(a => a.expectedAction.type === 'jump');
        if (jumpIdx >= 0) {
          setSequenceIndex(jumpIdx);
        }
        setStepCompleted(false);
      } else {
        setStepCompleted(false);
      }
      return;
    }
    // Destage placement - allowed when expected action is 'undo' (action sequence) or not a confirm step
    if (stagedMove.type === 'placement' && (expected.type === 'undo' || expected.type !== 'confirm')) {
      setStagedMove({ type: 'none', path: [] });
      if (expected.type === 'undo') {
        // Completing the undo action in a sequence
        completeCurrentAction();
      } else {
        setStepCompleted(false);
      }
    }
  }, [step, stagedMove, board, completeCurrentAction]);

  const handleKeyDown = useCallback((e) => {
    // Don't intercept when typing in an input or textarea
    const activeTag = document.activeElement?.tagName;
    if (activeTag === 'INPUT' || activeTag === 'TEXTAREA') {
        if (e.key === 'Escape') { document.activeElement.blur(); e.preventDefault(); }
        return;
    }

    const key = e.key.toLowerCase();
    const expected = step.expectedAction;
    const isShifted = e.shiftKey;

    // Move cursor - same as main game
    const moveDeltas = {
      'w': [-1, 0], 'arrowup': [-1, 0],
      's': [1, 0], 'arrowdown': [1, 0],
      'a': [0, -1], 'arrowleft': [0, -1],
      'd': [0, 1], 'arrowright': [0, 1],
    };

    // Focus shifting (Shift+A/D) - same as main game
    if (isShifted && (key === 'd' || e.key === 'ArrowRight')) {
      e.preventDefault();
      setKeyFocus('history');
      setFocusAnimating(true);
      setTimeout(() => setFocusAnimating(false), 500);
      if (isStackedLayout) { setActivePanelIndex(2); flashPanel(2); }
      // Check if this completes the tutorial step
      if (expected.type === 'focus-shift' && expected.target === 'history') {
        setTimeout(advanceStep, 300);
      }
      return;
    }
    if (isShifted && (key === 'a' || e.key === 'ArrowLeft')) {
      e.preventDefault();
      setKeyFocus('board');
      setFocusAnimating(true);
      setTimeout(() => setFocusAnimating(false), 500);
      if (isStackedLayout) { setActivePanelIndex(1); flashPanel(1); }
      if (expected.type === 'focus-shift' && expected.target === 'board') {
        setTimeout(advanceStep, 300);
      }
      return;
    }

    // History panel navigation (when focused on history)
    if (keyFocus === 'history' && logHistory.length > 0) {
      const delta = moveDeltas[key];
      if (delta && !isShifted) {
        e.preventDefault();
        const dir = delta[0]; // -1 for up (W/ArrowUp), +1 for down (S/ArrowDown)
        if (dir !== 0) {
          const maxIndex = logHistory.length - 1;
          const target = Math.max(0, Math.min(maxIndex, stepIndex + dir));
          if (target !== stepIndex) setStepIndex(target);
        }
        return;
      }
    }

    // Board camera controls (always allowed)
    const c = controlsRef.current;
    if (c && key === 'f') {
      e.preventDefault();
      c.fitAndCenter();
      // Check if this completes a key-press step (require transform if step asks for it)
      if (expected.type === 'key' && expected.key === 'f') {
        if (step.requiresTransform && !boardTransformed) {
          // Don't complete - user hasn't zoomed/panned yet
        } else {
          completeCurrentAction();
        }
      }
      return;
    }
    if (c && key === 'c') {
      e.preventDefault();
      c.centerAtCurrentScale();
      // Check if this completes a key-press step (require transform if step asks for it)
      if (expected.type === 'key' && expected.key === 'c') {
        if (step.requiresTransform && !boardTransformed) {
          // Don't complete - user hasn't zoomed/panned yet
        } else {
          completeCurrentAction();
        }
      }
      return;
    }

    // Tutorial navigation: [ to go back, ] to advance (only if allowed)
    if (e.key === '[') {
      e.preventDefault();
      goBackStep();
      return;
    }
    if (e.key === ']') {
      // Check if advancement is allowed (same logic as computeCanAdvance)
      let canAdvanceNow = false;

      // If tutorial has been completed, allow free navigation
      if (tutorialCompleted) {
        canAdvanceNow = true;
      } else if (stepIndex < maxStepReached) {
        canAdvanceNow = true;
      } else {
        // Block if there's an invalid staged jump (unless step explicitly allows it,
        // or this is an 'invalid-jump' step which requires staging an invalid jump)
        const hasInvalidJump = stagedMove?.type === 'jump' &&
                               stagedMove.path?.length > 0 &&
                               jumpPreview.invalidIndex !== -1;
        if (hasInvalidJump && !step.allowInvalidJumps && expected.type !== 'invalid-jump') {
          canAdvanceNow = false;
        } else if (expected.type === 'continue' || expected.type === 'finish') {
          canAdvanceNow = true;
        } else if (expected.type === 'invalid-jump') {
          if (hasInvalidJump) {
            const targetPos = step.highlightPos;
            if (!targetPos) {
              canAdvanceNow = true;
            } else if (stagedMove.path?.length > 0) {
              const lastJump = stagedMove.path[stagedMove.path.length - 1];
              canAdvanceNow = lastJump[0] === targetPos[0] && lastJump[1] === targetPos[1];
            }
          }
        } else if (expected.type === 'invalid-place') {
          // Must have attempted an invalid placement at the target row
          if (invalidPlacement && expected.targetRow !== undefined) {
            canAdvanceNow = invalidPlacement[0] === expected.targetRow;
          }
        } else if (expected.type === 'move-cursor' && expected.target && cursorPos) {
          canAdvanceNow = cursorPos[0] === expected.target[0] && cursorPos[1] === expected.target[1];
        } else if (expected.type === 'click') {
          canAdvanceNow = stepCompleted;
        } else if (expected.type === 'jump' && expected.pos) {
          // Account for pre-staged jumps and multi-jump sequences
          const prestagedCount = step.stagedJump?.path?.length || 0;
          const newJumpsRequired = step.highlightSequence?.length || 1;
          const expectedLength = prestagedCount + newJumpsRequired;
          // Must have exactly the expected number of jumps, with last one at expected position
          if (stagedMove?.type === 'jump' && stagedMove.path?.length === expectedLength) {
            const lastJump = stagedMove.path[stagedMove.path.length - 1];
            canAdvanceNow = lastJump[0] === expected.pos[0] && lastJump[1] === expected.pos[1];
          }
        } else {
          canAdvanceNow = stepCompleted;
        }
      }

      if (canAdvanceNow) {
        e.preventDefault();
        if (expected.type === 'finish') {
          setShowCompletion(true);
          setTutorialCompleted(true);
        } else {
          advanceStep();
        }
      }
      return;
    }

    // Cursor movement (only for steps that need it)
    if (allowsCursor) {
      const delta = moveDeltas[key];
      if (delta && !isShifted) {
        e.preventDefault();
        const base = cursorPos || step.cursorStart || step.phutballPos;
        const newR = Math.max(0, Math.min(ROWS - 1, base[0] + delta[0]));
        const newC = Math.max(0, Math.min(COLS - 1, base[1] + delta[1]));
        setCursorPos([newR, newC]);
        // Check if this completes a move-cursor step
        if (expected.type === 'move-cursor' &&
            newR === expected.target[0] && newC === expected.target[1]) {
          completeCurrentAction();
        }
        return;
      }
    }

    // Place stone
    if (allowsPlace && key === 'p') {
      e.preventDefault();
      handlePlace();
      return;
    }

    // Jump
    if (allowsJump && key === 'j') {
      e.preventDefault();
      handleJump();
      return;
    }

    // Backspace - undo
    if (e.key === 'Backspace' && allowsUndo) {
      e.preventDefault();
      handleUndo();
      return;
    }

    // Confirm move
    if (allowsConfirm && key === 'enter') {
      e.preventDefault();
      handleConfirm();
      return;
    }

    // Escape - cancel staged move
    if ((allowsPlace || allowsJump) && e.key === 'Escape' && stagedMove.type !== 'none') {
      e.preventDefault();
      setStagedMove({ type: 'none', path: [] });
      setJumpPreview({ jumpedPieces: [], usageCounts: {}, invalidIndex: -1, isValid: false });
      setStepCompleted(false);
      // On confirm steps, go back to the staging step (or reset sequence)
      if (expected.type === 'confirm') {
        if (rawStep.actionSequence) {
          // Reset to beginning of action sequence and re-stage initial state
          setSequenceIndex(0);
          if (rawStep.stagedMove) {
            setStagedMove(rawStep.stagedMove);
          } else if (rawStep.stagedJump) {
            setStagedMove({ type: 'jump', ...rawStep.stagedJump });
          }
        } else {
          goBackStep();
        }
      }
      return;
    }
  }, [step, rawStep, cursorPos, stagedMove, keyFocus, stepCompleted, advanceStep, goBackStep, onExit, board, jumpPreview, invalidPlacement, ballPos, unlocks, steps, allowsCursor, allowsPlace, allowsJump, allowsConfirm, allowsUndo, handlePlace, handleJump, handleConfirm, handleUndo, completeCurrentAction, isStackedLayout, flashPanel]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  // Keyboard navigation for pause/completion overlays
  useEffect(() => {
    const overlayOpen = showPauseMenu || showCompletion;
    if (!overlayOpen) {
      setOverlaySelectedIndex(0);
      return;
    }

    const handleOverlayKeyDown = (e) => {
      e.stopPropagation();
      const key = e.key;

      if (key === 'ArrowLeft' || key === 'a' || key === 'A' ||
          key === 'ArrowUp' || key === 'w' || key === 'W') {
        setOverlaySelectedIndex(i => Math.max(0, i - 1));
        e.preventDefault();
      } else if (key === 'ArrowRight' || key === 'd' || key === 'D' ||
                 key === 'ArrowDown' || key === 's' || key === 'S') {
        setOverlaySelectedIndex(i => Math.min(1, i + 1));
        e.preventDefault();
      } else if (key === 'Enter') {
        if (showPauseMenu) {
          if (overlaySelectedIndex === 0) setShowPauseMenu(false);
          else handleExit();
        } else if (showCompletion) {
          if (overlaySelectedIndex === 0) setShowCompletion(false);
          else handleExit();
        }
        e.preventDefault();
      } else if (key === 'Escape') {
        if (showPauseMenu) setShowPauseMenu(false);
        else if (showCompletion) setShowCompletion(false);
        e.preventDefault();
      }
    };

    window.addEventListener('keydown', handleOverlayKeyDown, true);
    return () => window.removeEventListener('keydown', handleOverlayKeyDown, true);
  }, [showPauseMenu, showCompletion, overlaySelectedIndex, handleExit]);

  const handleHoverCell = (r, c) => setHoverPos([r, c]);
  const handleLeaveBoard = () => setHoverPos(null);

  const handleCellClick = useCallback((r, c) => {
    // Click is unlocked at the click unlock step
    if (stepIndex < unlocks.click) return;

    const expected = step.expectedAction;
    // Clicking moves the cursor
    setCursorPos([r, c]);

    // Check if this completes the current step
    if (expected.type === 'click' &&
        r === expected.target[0] && c === expected.target[1]) {
      completeCurrentAction();
    }
    if (expected.type === 'move-cursor' &&
        r === expected.target[0] && c === expected.target[1]) {
      completeCurrentAction();
    }
  }, [step, stepIndex, unlocks, completeCurrentAction]);

  const registerBoardControls = useCallback((controls) => {
    controlsRef.current = controls;
  }, []);

  const handleBoardTransformed = useCallback(() => {
    setBoardTransformed(true);
  }, []);

  // Tutorial highlight is shown separately; hover always works
  const effectiveHoverPos = hoverPos;

  // Compute effective highlight position for multi-jump sequences and animations
  const computeEffectiveHighlight = () => {
    // Eight Doors animation - show current direction's landing
    if (step.animatedDemo === 'eight-doors') {
      // Only show highlight during 'highlight' phase
      if (eightDoorsPhase === 'highlight') {
        return EIGHT_DOORS_LANDINGS[eightDoorsIndex];
      }
      return null;
    }
    // If step has a highlight sequence, show the next target based on progress
    if (step.highlightSequence && step.highlightSequence.length > 0) {
      const prestagedCount = step.stagedJump?.path?.length || 0;
      const currentJumps = stagedMove?.type === 'jump' ? stagedMove.path?.length || 0 : 0;
      const newJumpsCompleted = currentJumps - prestagedCount;
      // Show the next highlight in sequence (or none if all completed)
      if (newJumpsCompleted >= 0 && newJumpsCompleted < step.highlightSequence.length) {
        return step.highlightSequence[newJumpsCompleted];
      }
      return null; // All jumps in sequence completed
    }
    // For placement steps, hide highlight once correct placement is staged
    if (step.expectedAction?.type === 'place' && step.highlightPos) {
      if (stagedMove?.type === 'placement' && stagedMove.path?.length > 0) {
        const [stagedR, stagedC] = stagedMove.path[0];
        if (stagedR === step.highlightPos[0] && stagedC === step.highlightPos[1]) {
          return null; // Hide highlight - correct placement staged
        }
      }
    }
    // For single jump steps, hide highlight once correct jump is staged
    if (step.expectedAction?.type === 'jump' && step.highlightPos && !step.highlightSequence) {
      if (stagedMove?.type === 'jump' && stagedMove.path?.length > 0) {
        const lastJump = stagedMove.path[stagedMove.path.length - 1];
        if (lastJump[0] === step.highlightPos[0] && lastJump[1] === step.highlightPos[1]) {
          return null; // Hide highlight - correct jump staged
        }
      }
    }
    // For invalid-place steps, hide highlight once invalid placement attempted
    if (step.expectedAction?.type === 'invalid-place' && step.highlightPos && invalidPlacement) {
      return null;
    }
    // For invalid-jump steps, hide highlight once invalid jump attempted
    if (step.expectedAction?.type === 'invalid-jump' && step.highlightPos && jumpPreview.invalidIndex >= 0) {
      return null;
    }
    // Fall back to static highlightPos
    return step.highlightPos || null;
  };
  const effectiveHighlightPos = computeEffectiveHighlight();

  // Compute animation-based staged move and jump preview for Eight Doors
  const computeAnimationState = () => {
    if (step.animatedDemo !== 'eight-doors') {
      return { animatedStagedMove: null, animatedJumpPreview: null };
    }

    const landing = EIGHT_DOORS_LANDINGS[eightDoorsIndex];
    const startPos = step.phutballPos;

    if (eightDoorsPhase === 'highlight') {
      return { animatedStagedMove: null, animatedJumpPreview: null };
    }

    if (eightDoorsPhase === 'executed') {
      // During executed phase, ball is at landing - no staged move overlay
      return { animatedStagedMove: null, animatedJumpPreview: null };
    }

    // 'staged' phase - show the jump preview
    // Build a fresh board for preview calculation (without the moved ball)
    const previewBoard = setBoardPieces(createEmptyBoard(), [
      { pos: startPos, type: TILE_TYPE.PHUTBALL },
      ...step.board,
    ]);

    // Build the staged move and preview for this direction
    const animStagedMove = { type: 'jump', path: [landing], startPos };
    const animJumpPreview = evaluateJumpSequence(previewBoard, startPos, [landing]);

    return { animatedStagedMove: animStagedMove, animatedJumpPreview: animJumpPreview };
  };
  const { animatedStagedMove, animatedJumpPreview } = computeAnimationState();

  // Effective values for GameBoard (use animation values when in animation)
  const effectiveStagedMove = animatedStagedMove || stagedMove;
  const effectiveJumpPreview = animatedJumpPreview || jumpPreview;
  const showAnimatedSegmentBar = step.animatedDemo === 'eight-doors' && eightDoorsPhase !== 'highlight';

  // Determine if current step allows advancement
  const computeCanAdvance = () => {
    // If tutorial has been completed, allow free navigation
    if (tutorialCompleted) return true;

    // Allow free forward navigation through already-visited steps
    if (stepIndex < maxStepReached) return true;

    const expected = step.expectedAction;
    if (!expected) return false;

    // Block if there's an invalid staged jump (unless step explicitly allows it for pedagogy,
    // or this is an 'invalid-jump' step which requires staging an invalid jump)
    const hasInvalidJump = stagedMove?.type === 'jump' &&
                           stagedMove.path?.length > 0 &&
                           jumpPreview.invalidIndex !== -1;
    if (hasInvalidJump && !step.allowInvalidJumps && expected.type !== 'invalid-jump') {
      return false;
    }

    // Continue/finish steps allow advance
    if (expected.type === 'continue' || expected.type === 'finish') return true;

    // Invalid-jump steps: require an invalid jump at the highlighted position
    if (expected.type === 'invalid-jump') {
      // Check jumpPreview directly - if invalidIndex >= 0, an invalid jump was attempted
      const hasInvalidAttempt = jumpPreview.invalidIndex >= 0;
      if (!hasInvalidAttempt) return false;
      // Position check using stagedMove path
      const targetPos = step.highlightPos;
      if (!targetPos || !stagedMove?.path?.length) return hasInvalidAttempt;
      const lastJump = stagedMove.path[stagedMove.path.length - 1];
      return lastJump[0] === targetPos[0] && lastJump[1] === targetPos[1];
    }

    // Invalid-place steps: require attempting to place in a forbidden row
    if (expected.type === 'invalid-place') {
      // Must have attempted an invalid placement at the target row
      if (!invalidPlacement) return false;
      if (expected.targetRow !== undefined) {
        return invalidPlacement[0] === expected.targetRow;
      }
      return true;
    }

    // Move-cursor: cursor must currently BE at target
    if (expected.type === 'move-cursor' && expected.target && cursorPos) {
      return cursorPos[0] === expected.target[0] && cursorPos[1] === expected.target[1];
    }

    // Click: must have actually clicked at target (uses stepCompleted state)
    if (expected.type === 'click') {
      return stepCompleted;
    }

    // Jump steps: must match EXACTLY - not more jumps, not fewer
    if (expected.type === 'jump' && expected.pos) {
      if (!stagedMove || stagedMove.type !== 'jump' || !stagedMove.path || stagedMove.path.length === 0) return false;

      // Account for pre-staged jumps and multi-jump sequences
      const prestagedCount = step.stagedJump?.path?.length || 0;
      // If highlightSequence exists, the user needs to complete that many new jumps
      const newJumpsRequired = step.highlightSequence?.length || 1;
      const expectedLength = prestagedCount + newJumpsRequired;

      // Must have exactly the expected number of jumps
      if (stagedMove.path.length !== expectedLength) return false;

      // The last jump must be the expected position
      const lastJump = stagedMove.path[stagedMove.path.length - 1];
      return lastJump[0] === expected.pos[0] && lastJump[1] === expected.pos[1];
    }

    // Other steps use stepCompleted state
    return stepCompleted;
  };
  const canAdvance = computeCanAdvance();

  // Build tutorial mode props for LogPanel
  const handleNext = () => {
    if (step.expectedAction?.type === 'finish') {
      setShowCompletion(true);
      setTutorialCompleted(true);
    } else {
      advanceStep();
    }
  };

  const isActionStep = step.expectedAction?.type !== 'continue' && step.expectedAction?.type !== 'finish';

  // Only show minimap preview for action steps, and hide once completed
  // For steps with active jump chains, derive the preview from live stagedMove state
  const livePreview = useMemo(() => {
    const actionType = step.expectedAction?.type;
    const hasInvalid = jumpPreview?.invalidIndex >= 0;
    if (stagedMove.type === 'jump' && stagedMove.path?.length > 0 && stagedMove.startPos) {
      const ballStart = stagedMove.startPos;
      // If there's an invalid jump staged, always show the invalid chain with backspace hint
      if (hasInvalid) {
        return { type: 'jump', ballStart, jumpPath: stagedMove.path, invalidIndex: jumpPreview.invalidIndex, label: '⌫' };
      }
      if (actionType === 'confirm' || actionType === 'undo') {
        const label = actionType === 'confirm' ? 'ENTER' : '⌫';
        return { type: 'jump', ballStart, jumpPath: stagedMove.path, label };
      }
      if (actionType === 'jump') {
        // Show staged path + next correct target, animate only the next jump
        const remainingTargets = step.highlightSequence
          ? step.highlightSequence.slice(stagedMove.path.length)
          : step.highlightPos ? [step.highlightPos] : [];
        if (remainingTargets.length > 0) {
          const fullPath = [...stagedMove.path, remainingTargets[0]];
          return { type: 'jump', ballStart, jumpPath: fullPath, animateFrom: stagedMove.path.length };
        }
      }
    }
    return null;
  }, [stagedMove, step.expectedAction?.type, step.highlightPos, step.highlightSequence, jumpPreview?.invalidIndex]);
  const activePreview = isActionStep && !canAdvance ? (livePreview || tutorialPreview) : null;

  const tutorialModeProps = {
    stepIndex,
    totalSteps: steps.length,
    instruction: step.instruction,
    detail: typeof step.detail === 'function' ? step.detail(isStackedLayout) : step.detail,
    hint: (jumpPreview?.invalidIndex >= 0)
      ? (isStackedLayout ? <span>Tap <kbd>Undo</kbd></span> : <span>Press <kbd>BACKSPACE</kbd> to undo</span>)
      : getHintText(step.expectedAction, isStackedLayout),
    onPrev: goBackStep,
    onNext: canAdvance ? handleNext : null,
    canAdvance,
    actionRequired: isActionStep,
    stepDone: isActionStep && canAdvance,
    needsUndo: jumpPreview?.invalidIndex >= 0,
    hideControls: isStackedLayout,
    activeControls: getActiveControls(step.expectedAction, jumpPreview?.invalidIndex >= 0, isActionStep && canAdvance, { requiresTransform: step.requiresTransform, boardTransformed }),
  };

  // Derive history from steps up to and including current step
  const tutorialHistory = steps.slice(0, stepIndex + 1).map(s => s.instruction);

  // Format cursor/hover indicators same as App.js
  const makeIndicator = (position) => formatBoardCoordinate({
    position,
    numRows: ROWS,
    numCols: COLS,
    coordSystem,
    viewPerspective: 1,
  });
  const cursorIndicator = cursorPos ? makeIndicator(cursorPos) : { rowLabel: '—', colLabel: '—' };
  const hoverIndicator = hoverPos ? makeIndicator(hoverPos) : { rowLabel: '—', colLabel: '—' };

  // ===== PANELS (same structure as App.js) =====
  const logPanel = (
    <LogPanel
      logHistory={logHistory}
      initialBoardText={null}
      tutorialMode={tutorialModeProps}
    />
  );

  const boardPanel = (
    <div className={`board-container${keyFocus === 'board' && focusAnimating ? ' tutorial-focus-pulse' : ''}${isActionStep && canAdvance ? ' tutorial-step-complete' : ''}`}>
      <div className="board-status-bar">
        <div className="board-indicator-group" aria-live="polite">
          <div className="coord-indicator">
            <div className="coord-indicator__label">Cursor</div>
            <div className="coord-indicator__value">
              <span className="coord-indicator__axis">R</span>
              <span>{cursorIndicator.rowLabel}</span>
              <span className="coord-indicator__divider">·</span>
              <span className="coord-indicator__axis">C</span>
              <span>{cursorIndicator.colLabel}</span>
            </div>
          </div>
          <div className="coord-indicator coord-indicator--hover">
            <div className="coord-indicator__label">Hover</div>
            <div className="coord-indicator__value">
              <span className="coord-indicator__axis">R</span>
              <span>{hoverIndicator.rowLabel}</span>
              <span className="coord-indicator__divider">·</span>
              <span className="coord-indicator__axis">C</span>
              <span>{hoverIndicator.colLabel}</span>
            </div>
          </div>
        </div>
        <button
          type="button"
          className="board-toggle"
          onClick={(e) => { toggleCoordSystem(); e.currentTarget.blur(); }}
        >
          <CoordinateSystemIcon mode={coordSystem} />
          <span>{coordSystem === 'matrix' ? 'matrix' : 'endzone relative'}</span>
        </button>
        <button
          type="button"
          className="board-toggle"
          onClick={(e) => { setShowMinimap(v => !v); e.currentTarget.blur(); }}
          aria-label={showMinimap ? 'Hide minimap' : 'Show minimap'}
          title={showMinimap ? 'Hide minimap' : 'Show minimap'}
        >
          <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <rect x="1" y="1" width="14" height="10" rx="1.5" />
            <circle cx="8" cy="6" r="2" />
            {!showMinimap && <line x1="2" y1="12" x2="14" y2="0" strokeWidth="2" stroke="currentColor" />}
          </svg>
          <span>{showMinimap ? 'minimap' : 'minimap off'}</span>
        </button>
      </div>
      <GameBoard
        board={board}
        phutballPos={ballPos}
        onCellClick={handleCellClick}
        onHoverCell={handleHoverCell}
        onLeaveBoard={handleLeaveBoard}
        coordSystem={coordSystem}
        viewPerspective={1}
        cursorPos={cursorPos}
        hoverPos={effectiveHoverPos}
        stagedMove={effectiveStagedMove}
        jumpPreview={effectiveJumpPreview}
        invalidPlacement={invalidPlacement}
        onControlsReady={registerBoardControls}
        showSegmentBar={showAnimatedSegmentBar || (stagedMove.type === 'jump' && stagedMove.path?.length > 0)}
        tutorialHighlightPos={effectiveHighlightPos}
        tutorialPreview={activePreview}
        onBoardTransformed={handleBoardTransformed}
        showMinimap={showMinimap}
      />
      <div className="action-bar">
        <button
          type="button"
          className="board-recenter-btn"
          onClick={(e) => {
            controlsRef.current?.fitAndCenter?.(250);
            e.currentTarget.blur();
            if (isStackedLayout) {
              const expected = step.expectedAction;
              if (expected?.type === 'key' && (expected.key === 'f' || expected.key === 'c')) {
                if (!step.requiresTransform || boardTransformed) {
                  completeCurrentAction();
                }
              }
            }
          }}
          aria-label="Recenter board"
          title="Recenter board"
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
            <circle cx="8" cy="8" r="3" />
            <line x1="8" y1="0.5" x2="8" y2="3.5" />
            <line x1="8" y1="12.5" x2="8" y2="15.5" />
            <line x1="0.5" y1="8" x2="3.5" y2="8" />
            <line x1="12.5" y1="8" x2="15.5" y2="8" />
          </svg>
        </button>
        <button disabled={!allowsPlace || !cursorPos || (step.expectedAction?.type === 'confirm' && stepCompleted)} onClick={(e) => { handlePlace(); e.currentTarget.blur(); }}>Place</button>
        <button disabled={!allowsJump || !cursorPos || (step.expectedAction?.type === 'confirm' && stepCompleted)} onClick={(e) => { handleJump(); e.currentTarget.blur(); }}>Jump</button>
        <button disabled={!allowsConfirm || stagedMove.type === 'none' || step.expectedAction?.type !== 'confirm'} onClick={(e) => { handleConfirm(); e.currentTarget.blur(); }}>Confirm</button>
        <button disabled={!allowsUndo} onClick={(e) => { handleUndo(); e.currentTarget.blur(); }}>Undo</button>
      </div>
    </div>
  );

  const historyPanel = (
    <HistoryPanel
      history={tutorialHistory}
      currentIndex={tutorialHistory.length - 1}
      hideLiveEntry
      onHistoryClick={(index) => { if (index >= 0 && index < steps.length) setStepIndex(index); }}
      onPreviewHistoryEntry={() => {}}
      viewMode="linear"
      setViewMode={() => {}}
      historyTree={null}
      onSelectTreeNode={() => {}}
      onPreviewTreeEdge={() => {}}
      onReplayStateChange={() => {}}
      keyFocus="board"
      onRequestFocus={() => {}}
      focusedHistoryIndex={stepIndex}
      registerToggleJumpEntry={() => {}}
      onRequestBranch={() => {}}
      coordSystem={coordSystem}
      boardRows={ROWS}
      boardCols={COLS}
      viewPerspective={1}
    />
  );

  // ===== RENDER (carbon copy of App.js structure) =====
  const appClassName = `app-container${isStackedLayout ? ' app-container--stacked' : ''}`;
  const mainClassName = `main-content${isStackedLayout ? ' stacked' : ''}`;
  const pagerClasses = ['panel-pager'];
  if (isPagerDragging) pagerClasses.push('panel-pager--dragging');
  if (forceNoTransition) pagerClasses.push('panel-pager--no-transition');
  const pagerStyle = isStackedLayout
    ? {
        width: `${TUTORIAL_PANEL_CONFIG.length * 100}%`,
        transform: `translateX(calc(-${activePanelIndex * 100 / TUTORIAL_PANEL_CONFIG.length}% + ${swipeOffsetPct / TUTORIAL_PANEL_CONFIG.length}%))`,
      }
    : undefined;
  const panelContentMap = { log: logPanel, board: boardPanel, history: historyPanel };

  return (
    <div
      className={appClassName}
      ref={isStackedLayout ? swipeContainerRef : undefined}
    >
      {/* Menu toggle button - same as App.js but opens exit action */}
      <button
        className="game-menu-toggle"
        onClick={(e) => {
          e.currentTarget.blur();
          setShowPauseMenu(true);
        }}
        aria-label="Pause tutorial"
      >
        <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
          <rect x="2" y="4" width="16" height="2" rx="1" />
          <rect x="2" y="9" width="16" height="2" rx="1" />
          <rect x="2" y="14" width="16" height="2" rx="1" />
        </svg>
      </button>

      <Header currentPlayer={1} showTutorialHint={!isStackedLayout || activePanelIndex === 1} tutorialStatus={tutorialModeProps} isStackedLayout={isStackedLayout} activePanelIndex={activePanelIndex} />

      {isStackedLayout && (
        <div className="panel-indicator-bar">
          <div className={`panel-indicator-track${activePanelIndex === 0 && isActionStep && !canAdvance ? ' panel-indicator-intro' : ''}`}>
            {TUTORIAL_PANEL_CONFIG.map((panel, idx) => (
              <button
                key={`${panel.id}-dot`}
                className={`panel-dot${activePanelIndex === idx ? ' active' : ''}`}
                onClick={() => setActivePanelIndex(idx)}
                aria-label={panel.label}
              />
            ))}
          </div>
        </div>
      )}

      <main className={mainClassName}>
        {isStackedLayout ? (
          <div className={pagerClasses.join(' ')} style={pagerStyle}>
            {TUTORIAL_PANEL_CONFIG.map((panel, panelIdx) => {
              const innerClasses = ['panel-page-inner'];
              if (panel.id === 'board') innerClasses.push('panel-page-inner--board');
              if (panel.id === 'history') innerClasses.push('panel-page-inner--history');
              return (
                <section key={panel.id} className={`panel-page${panelFlash === panelIdx ? ' panel-page--flash' : ''}`} aria-label={panel.label}>
                  <div className={innerClasses.join(' ')}>
                    {panelContentMap[panel.id]}
                  </div>
                </section>
              );
            })}
          </div>
        ) : (
          <>
            {logPanel}
            {boardPanel}
            {historyPanel}
          </>
        )}
      </main>

      {/* Pause menu overlay */}
      {showPauseMenu && (
        <div className="tutorial-complete-overlay" onClick={() => setShowPauseMenu(false)} onTouchEnd={(e) => { if (e.target === e.currentTarget) { e.preventDefault(); setShowPauseMenu(false); } }}>
          <div className="tutorial-complete" onClick={e => e.stopPropagation()} onTouchEnd={e => e.stopPropagation()}>
            <h2>Paused</h2>
            <p>Tutorial progress is saved.</p>
            <div className="tutorial-complete__buttons">
              <button
                className={`tutorial-complete__button tutorial-complete__button--secondary${overlaySelectedIndex === 0 ? ' selected' : ''}`}
                onClick={() => setShowPauseMenu(false)}
                onMouseEnter={() => setOverlaySelectedIndex(0)}
              >
                Return to Tutorial
              </button>
              <button
                className={`tutorial-complete__button${overlaySelectedIndex === 1 ? ' selected' : ''}`}
                onClick={handleExit}
                onMouseEnter={() => setOverlaySelectedIndex(1)}
              >
                Return to Menu
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Completion overlay */}
      {showCompletion && (
        <div className="tutorial-complete-overlay">
          <div className="tutorial-complete">
            <h2>Well Done!</h2>
            <p>You're ready to play.</p>
            <div className="tutorial-complete__buttons">
              <button
                className={`tutorial-complete__button tutorial-complete__button--secondary${overlaySelectedIndex === 0 ? ' selected' : ''}`}
                onClick={() => setShowCompletion(false)}
                onMouseEnter={() => setOverlaySelectedIndex(0)}
              >
                Return to Tutorial
              </button>
              <button
                className={`tutorial-complete__button${overlaySelectedIndex === 1 ? ' selected' : ''}`}
                onClick={handleExit}
                onMouseEnter={() => setOverlaySelectedIndex(1)}
              >
                Return to Menu
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Tutorial;
